import { motion } from "framer-motion";
import { ArrowRight, Home, Minus, Plus, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";

const RegistrationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Search state
  const [searchMobile, setSearchMobile] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [existingRegistration, setExistingRegistration] = useState(null);
  const [existingDirectory, setExistingDirectory] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isNewEntry, setIsNewEntry] = useState(false);

  // Add these new state variables near other useState declarations
const [originalTotal, setOriginalTotal] = useState(0);
const [showAdditionalPayment, setShowAdditionalPayment] = useState(false);
const [additionalPaymentAmount, setAdditionalPaymentAmount] = useState(0);
const [additionalPaymentLoading, setAdditionalPaymentLoading] = useState(false);

  // Form data state - UPDATED ACCOMPANY STRUCTURE
  const [formData, setFormData] = useState({
    name: "",
    batch: "",
    mobile: "",
    email: "",
    tshirtSize: "",
    accompany: {
      spouse: 0,
      guest: 0,
      driver: 0,
      childAbove5: 0,
      childBelow5: 0,
    },
    childAbove5TshirtSize: "",
    childBelow5TshirtSize: "",
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [batchPricing, setBatchPricing] = useState({});
  const [tShirtSize, setTShirtSize] = useState([]);
  const [childAbove5TShirtSize, setChildAbove5TShirtSize] = useState([]);
  const [childBelow5TShirtSize, setChildBelow5TShirtSize] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gender, setGender] = useState("Male");
  const [showChildAbove5TShirtModal, setShowChildAbove5TShirtModal] =
    useState(false);
  const [showChildBelow5TShirtModal, setShowChildBelow5TShirtModal] =
    useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;
  const { user, getAuthHeader } = useAuth();

  // Store original mobile from search
  const [originalMobile, setOriginalMobile] = useState("");

  const stringToArray = (str) => {
    if (!str) return [];
    return str
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s);
  };

  const arrayToString = (arr) => {
    if (!arr || arr.length === 0) return "";
    return arr.join(",");
  };

  // Fetch batch pricing on mount
  useEffect(() => {
    const fetchBatchPricing = async () => {
      try {
        const data = await api.batchPricing.getAll();
        const pricingMap = {};
        data.forEach((bp) => {
          pricingMap[bp.batchName] = {
            participantFee: bp.participantFee,
            spouseFee: bp.spouseFee || 0,
            guestFee: bp.guestFee || 0,
            driverFee: bp.driverFee || 0,
            above5Fee: bp.above5Fee || 0,
            below5Fee: bp.below5Fee || 0,
          };
        });
        setBatchPricing(pricingMap);
      } catch (err) {
        console.error("Error fetching batch pricing:", err);
      }
    };

    fetchBatchPricing();
  }, []);

  // Fetch T-shirt sizes when gender changes
  useEffect(() => {
    if (!gender) return;

    const fetchTShirtSizes = async () => {
      try {
        const data = await api.lookup.getTShirtByGender(gender);
        const tShirtSizes = data.map((item) => item.name);
        setTShirtSize(tShirtSizes);
      } catch (err) {
        console.error("Error fetching t-shirt sizes:", err);
      }
    };

    fetchTShirtSizes();
  }, [gender]);

  // Fetch T-shirt sizes for children above 5
  useEffect(() => {
    const fetchChildAbove5TShirtSizes = async () => {
      try {
        const data = await api.lookup.getTShirtByGender("Child>5");
        const tShirtSizes = data.map((item) => item.name);
        setChildAbove5TShirtSize(tShirtSizes);
      } catch (err) {
        console.error(
          "Error fetching t-shirt sizes for children above 5:",
          err
        );
      }
    };

    fetchChildAbove5TShirtSizes();
  }, []);

  // Fetch T-shirt sizes for children below 5
  useEffect(() => {
    const fetchChildBelow5TShirtSizes = async () => {
      try {
        const data = await api.lookup.getTShirtByGender("Child<5");
        const tShirtSizes = data.map((item) => item.name);
        setChildBelow5TShirtSize(tShirtSizes);
      } catch (err) {
        console.error(
          "Error fetching t-shirt sizes for children below 5:",
          err
        );
      }
    };

    fetchChildBelow5TShirtSizes();
  }, []);

  const [searchParams] = useSearchParams();

  // Handle mobile parameter from URL
  useEffect(() => {
    const mobileParam = searchParams.get("mobile");
    if (mobileParam) {
      setSearchMobile(mobileParam);

      if (mobileParam.length === 11) {
        handleSearchRegistrationWithMobile(mobileParam);
      } else {
        setSearchError("Mobile number must be 11 digits");
      }
    }
  }, [searchParams]);

  // Search function with mobile parameter
  const handleSearchRegistrationWithMobile = async (mobileNumber) => {
    console.log("Searching for mobile:", mobileNumber);

    if (!mobileNumber || mobileNumber.length < 11) {
      setSearchError("Please enter a valid 11-digit mobile number");
      return;
    }

    try {
      setSearchLoading(true);
      setSearchError("");
      setExistingRegistration(null);
      setExistingDirectory(null);
      setIsNewEntry(false);

      // First, search for existing registration
      const registrationResponse = await api.registration.getMyRegistrations(
        mobileNumber
      );

      if (registrationResponse && registrationResponse.length > 0) {
        // Found existing registration
        const registration = registrationResponse[0];
        setExistingRegistration(registration);

        // Auto-fill form with registration data - UPDATED FOR NEW STRUCTURE
        setFormData({
          name: registration.name || "",
          batch: registration.batch || "",
          mobile: registration.mobile || mobileNumber,
          email: registration.email || "",
          tshirtSize: registration.tshirtSize || "",
          accompany: {
            spouse: registration.accompany?.spouse || 0,
            guest: registration.accompany?.guest || 0,
            driver: registration.accompany?.driver || 0,
            childAbove5: registration.accompany?.childAbove5 || 0,
            childBelow5: registration.accompany?.childBelow5 || 0,
          },
          childAbove5TshirtSize: registration.childAbove5TshirtSize || "",
          childBelow5TshirtSize: registration.childBelow5TshirtSize || "",
        });

        setOriginalMobile(registration.mobile || mobileNumber);
        setGender(registration.gender || "Male");

        setShowForm(true);
        setIsNewEntry(false);

        // Check payment status
        if (registration.paymentStatus === "Paid") {
          setSearchError(
            "This registration is already paid and cannot be modified."
          );
        }

        return;
      }

      // If no registration found, search directory
      const directoryResponse = await api.directory.searchByMobile(
        mobileNumber
      );

      if (directoryResponse && directoryResponse.length > 0) {
        // Found existing directory - use as base for new registration
        const directory = directoryResponse[0];
        setExistingDirectory(directory);

        // Auto-fill form with directory data - UPDATED FOR NEW STRUCTURE
        setFormData({
          name: directory.name || "",
          batch: directory.batch || "",
          mobile: directory.mobile || mobileNumber,
          email: directory.email || "",
          tshirtSize: "",
          accompany: {
            spouse: 0,
            guest: 0,
            driver: 0,
            childAbove5: 0,
            childBelow5: 0,
          },
          childAbove5TshirtSize: "",
          childBelow5TshirtSize: "",
        });

        setOriginalMobile(directory.mobile || mobileNumber);
        setShowForm(true);
        setIsNewEntry(true);
      } else {
        // No existing record found - start fresh
        setFormData({
          name: "",
          batch: "",
          mobile: mobileNumber,
          email: "",
          tshirtSize: "",
          accompany: {
            spouse: 0,
            guest: 0,
            driver: 0,
            childAbove5: 0,
            childBelow5: 0,
          },
          childAbove5TshirtSize: "",
          childBelow5TshirtSize: "",
        });

        setOriginalMobile(mobileNumber);
        setShowForm(true);
        setIsNewEntry(true);
      }
    } catch (error) {
      console.error("Error searching:", error);
      setSearchError("Failed to search. Please try again.");
    } finally {
      setSearchLoading(false);
    }
  };

  // Original search function
  const handleSearchRegistration = async () => {
    await handleSearchRegistrationWithMobile(searchMobile);
  };

  const handleResetSearch = () => {
    setSearchMobile("");
    setExistingRegistration(null);
    setExistingDirectory(null);
    setShowForm(false);
    setIsNewEntry(false);
    setFormData({
      name: "",
      batch: "",
      mobile: "",
      email: "",
      tshirtSize: "",
      accompany: {
        spouse: 0,
        guest: 0,
        driver: 0,
        childAbove5: 0,
        childBelow5: 0,
      },
      childAbove5TshirtSize: "",
      childBelow5TshirtSize: "",
    });
    setOriginalMobile("");
    setGender("Male");
    setSearchError("");
  };

  // Ensure mobile field is always set to originalMobile
  useEffect(() => {
    if (originalMobile) {
      setFormData((prev) => ({
        ...prev,
        mobile: originalMobile,
      }));
    }
  }, [originalMobile]);

  const handleInputChange = (field, value) => {
    if (field === "mobile") {
      return; // Don't allow mobile to be changed
    }

    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleAccompanyChange = (type, inc) => {
    const newCount = Math.max(0, formData.accompany[type] + inc);

    setFormData((prev) => {
      // Only child categories have t-shirt size options
      let sizeField = "";
      if (type === "childAbove5") {
        sizeField = "childAbove5TshirtSize";
      } else if (type === "childBelow5") {
        sizeField = "childBelow5TshirtSize";
      }

      if (sizeField) {
        const currentSizes = stringToArray(prev[sizeField]);
        let updatedSizes = currentSizes;

        if (newCount < prev.accompany[type]) {
          // Decreasing count - remove last size
          updatedSizes = currentSizes.slice(0, newCount);
        } else if (newCount === 0) {
          updatedSizes = [];
        }

        return {
          ...prev,
          accompany: {
            ...prev.accompany,
            [type]: newCount,
          },
          [sizeField]: arrayToString(updatedSizes),
        };
      } else {
        // For spouse, guest, driver - no t-shirt size selection needed
        return {
          ...prev,
          accompany: {
            ...prev.accompany,
            [type]: newCount,
          },
        };
      }
    });
  };

  const handleTShirtSizeModalSelect = (type, size, action) => {
    const currentSizes = stringToArray(formData[type]);
    const maxSelections =
      formData.accompany[
        type === "childAbove5TshirtSize" ? "childAbove5" : "childBelow5"
      ];

    // Count occurrences of the selected size
    const sizeCount = currentSizes.filter((s) => s === size).length;

    let newSizes = [...currentSizes];

    if (action === "increase") {
      // Increase count if we haven't reached max selections
      if (currentSizes.length < maxSelections) {
        newSizes.push(size);
      }
    } else if (action === "decrease") {
      // Decrease count if this size exists
      if (sizeCount > 0) {
        const indexToRemove = newSizes.lastIndexOf(size);
        if (indexToRemove > -1) {
          newSizes.splice(indexToRemove, 1);
        }
      }
    } else if (action === "remove") {
      // Remove all occurrences of this size
      newSizes = newSizes.filter((s) => s !== size);
    }

    setFormData((prev) => ({
      ...prev,
      [type]: arrayToString(newSizes),
    }));
  };

  const handleClearSelection = (type) => {
    setFormData((prev) => ({
      ...prev,
      [type]: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";

    // Mobile validation - always use originalMobile
    const mobileToValidate = originalMobile || formData.mobile;
    if (!mobileToValidate || !mobileToValidate.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^\d{11}$/.test(mobileToValidate)) {
      newErrors.mobile = "Must be 11 digits";
    }

    // Validate child above 5 t-shirt sizes
    const childAbove5Sizes = stringToArray(formData.childAbove5TshirtSize);
    if (
      formData.accompany.childAbove5 > 0 &&
      childAbove5Sizes.length !== formData.accompany.childAbove5
    ) {
      newErrors.childAbove5TshirtSize = `Please select ${formData.accompany.childAbove5} t-shirt size(s) for ${formData.accompany.childAbove5} child(ren) above 5`;
    }

    // Validate child below 5 t-shirt sizes
    const childBelow5Sizes = stringToArray(formData.childBelow5TshirtSize);
    if (
      formData.accompany.childBelow5 > 0 &&
      childBelow5Sizes.length !== formData.accompany.childBelow5
    ) {
      newErrors.childBelow5TshirtSize = `Please select ${formData.accompany.childBelow5} t-shirt size(s) for ${formData.accompany.childBelow5} child(ren) below 5`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateTotal = () => {
    const batch = batchPricing["Arts"];
    if (!batch) return 0;

    return (
      batch.participantFee +
      formData.accompany.spouse * (batch.spouseFee || 0) +
      formData.accompany.guest * (batch.guestFee || 0) +
      formData.accompany.driver * (batch.driverFee || 0) +
      formData.accompany.childAbove5 * (batch.above5Fee || 0) +
      formData.accompany.childBelow5 * (batch.below5Fee || 0)
    );
  };

  // Track total amount changes for additional payment
useEffect(() => {
  if (existingRegistration?.paymentStatus === "Success") {
    const currentTotal = calculateTotal();
    const original = existingRegistration.totalAmount || 0;
    
    if (currentTotal > original) {
      setAdditionalPaymentAmount(currentTotal - original);
      setShowAdditionalPayment(true);
    } else {
      setShowAdditionalPayment(false);
      setAdditionalPaymentAmount(0);
    }
  }
}, [formData.accompany, existingRegistration]);

  const handleSubmit = async () => {
    const mobileToUse = originalMobile || formData.mobile;

    if (!validateForm()) {
      console.log("Validation failed with errors:", errors);
      return;
    }

    if (!formData.name || !formData.name.trim()) {
      alert("Please enter a name");
      return;
    }

    if (!mobileToUse || !/^\d{11}$/.test(mobileToUse)) {
      alert("Please ensure mobile number is 11 digits");
      return;
    }

    setLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("batch", formData.batch);
      formDataToSend.append("mobile", mobileToUse);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("tshirtSize", formData.tshirtSize);
      formDataToSend.append("gender", gender);
      formDataToSend.append("accompanySpouse", formData.accompany.spouse);
      formDataToSend.append("accompanyGuest", formData.accompany.guest);
      formDataToSend.append("accompanyDriver", formData.accompany.driver);
      formDataToSend.append(
        "accompanyChildAbove5",
        formData.accompany.childAbove5
      );
      formDataToSend.append(
        "accompanyChildBelow5",
        formData.accompany.childBelow5
      );
      formDataToSend.append(
        "childAbove5TshirtSize",
        formData.childAbove5TshirtSize || ""
      );
      formDataToSend.append(
        "childBelow5TshirtSize",
        formData.childBelow5TshirtSize || ""
      );

      if (user?.id) {
        formDataToSend.append("userId", user.id.toString());
      }

      let data;
      if (existingRegistration && existingRegistration.id) {
        // Update existing registration
        data = await api.registration.update(
          existingRegistration.id,
          formDataToSend
        );
      } else {
        // Create new registration
        data = await api.registration.create(formDataToSend);
      }

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

      return data;
    } catch (error) {
      console.error("Error submitting registration:", error);
      alert(
        `Registration ${existingRegistration ? "update" : "failed"}: ${
          error.message
        }`
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (participantData) => {
    try {
      const payload = {
        name: formData.name,
        batch: formData.batch,
        mobile: originalMobile || formData.mobile,
        email: formData.email,
        tshirtSize: formData.tshirtSize,
        accompanySpouse: formData.accompany.spouse,
        accompanyGuest: formData.accompany.guest,
        accompanyDriver: formData.accompany.driver,
        accompanyChildAbove5: formData.accompany.childAbove5,
        accompanyChildBelow5: formData.accompany.childBelow5,
        totalAmount: calculateTotal(),
        order_id: participantData?.orderId || `order_${Date.now()}`,
        participantId: participantData?.id || existingRegistration?.id,
        userId: user?.id,
        childAbove5TshirtSize: formData.childAbove5TshirtSize,
        childBelow5TshirtSize: formData.childBelow5TshirtSize,
      };

      const data = await api.payment.create(payload);

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert(data.error || "Failed to get checkout URL");
      }
    } catch (error) {
      console.error("Payment request failed", error);
      alert("Payment request failed. Try again.");
    }
  };

  const handleAdditionalPayment = async () => {
    if (!validateForm()) {
      return;
    }
  
    setAdditionalPaymentLoading(true);
    try {
      const payload = {
        participantId: existingRegistration.id,
        name: formData.name,
        batch: formData.batch,
        mobile: originalMobile || formData.mobile,
        email: formData.email,
        tshirtSize: formData.tshirtSize,
        accompanySpouse: formData.accompany.spouse,
        accompanyGuest: formData.accompany.guest,
        accompanyDriver: formData.accompany.driver,
        accompanyChildAbove5: formData.accompany.childAbove5,
        accompanyChildBelow5: formData.accompany.childBelow5,
        totalAmount: additionalPaymentAmount,
        order_id: `add_${existingRegistration.id}_${Date.now()}`,
        childAbove5TshirtSize: formData.childAbove5TshirtSize,
        childBelow5TshirtSize: formData.childBelow5TshirtSize,
        isAdditionalPayment: true // Flag to indicate this is an additional payment
      };
  
      const data = await api.payment.createAdditional(payload);
  
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert(data.error || "Failed to get checkout URL");
      }
    } catch (error) {
      console.error("Additional payment request failed", error);
      alert("Additional payment request failed. Try again.");
    } finally {
      setAdditionalPaymentLoading(false);
    }
  };

  const handleSave = async () => {
    if (existingRegistration && existingRegistration.paymentStatus === "Paid") {
      alert("This registration is already paid and cannot be modified.");
      return;
    }

    if (existingRegistration?.paymentStatus === "Success" && showAdditionalPayment) {
      await handleAdditionalPayment();
      return;
    }

    const submitted = await handleSubmit();
    if (submitted) {
      await handlePayment(submitted);
    }
  };

  const TShirtSizeModal = ({
    isOpen,
    onClose,
    onSelect,
    selectedSizesStr,
    title,
    maxSelections,
    availableSizes,
    onClear,
  }) => {
    if (!isOpen) return null;

    const selectedSizes = stringToArray(selectedSizesStr);

    // Calculate count of each selected size
    const sizeCounts = selectedSizes.reduce((acc, size) => {
      acc[size] = (acc[size] || 0) + 1;
      return acc;
    }, {});

    const totalSelected = selectedSizes.length;
    const remainingToSelect = maxSelections - totalSelected;

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-42">
        <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-6 w-full max-w-md relative">
          {/* Close button - Top right */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-red-500 hover:text-gray-700 transition-colors"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>

          <h3 className="text-xl font-bold text-gray-800 mb-2 pr-8">{title}</h3>
          <div className="mb-4">
            <p className="text-gray-600">
              Select <span className="font-bold">{maxSelections}</span> -
              Remaining: <span className="font-bold">{remainingToSelect}</span>
            </p>
            <div>
              <span>
                (Click <span className="font-medium text-green-600">+</span> to
                add a size and 
                <span className="font-medium text-red-600"> -</span> to remove a
                size)
              </span>
            </div>
          </div>

          {/* Clear button */}
          {selectedSizes.length > 0 && (
            <div className="mb-4 flex justify-end">
              <button
                onClick={onClear}
                className="px-3 py-1 text-sm bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
              >
                Clear All
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mb-6">
            {availableSizes.map((size) => {
              const selectedCount = sizeCounts[size] || 0;
              const canIncrease = remainingToSelect > 0;
              const canDecrease = selectedCount > 0;

              return (
                <div
                  key={size}
                  className={`p-3 rounded-xl font-medium transition-all ${
                    selectedCount > 0
                      ? "bg-gradient-to-br from-green-500 to-blue-500 text-white shadow-lg"
                      : "bg-white text-gray-700 border border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-lg">{size}</span>
                      {selectedCount > 0 && (
                        <span className="text-white font-bold">
                          × {selectedCount}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Decrease Button */}
                      <button
                        onClick={() => onSelect(size, "decrease")}
                        disabled={!canDecrease}
                        className={`p-1 rounded-full ${
                          canDecrease
                            ? "bg-red-100 text-red-600 hover:bg-red-200"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                        }`}
                        title="Decrease"
                      >
                        <Minus size={18} />
                      </button>

                      {/* Increase Button */}
                      <button
                        onClick={() => onSelect(size, "increase")}
                        disabled={!canIncrease}
                        className={`p-1 rounded-full ${
                          canIncrease
                            ? "bg-green-100 text-green-600 hover:bg-green-200"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                        }`}
                        title="Increase"
                      >
                        <Plus size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-between items-center mb-6">
            <div className="text-sm text-gray-600">
              Selected: <span className="font-bold">{totalSelected}</span>/
              {maxSelections}
            </div>
            {selectedSizes.length > 0 && (
              <div className="text-sm text-gray-700 max-w-[60%]">
                <span className="font-medium">Summary:</span>{" "}
                {Object.entries(sizeCounts)
                  .map(([size, count]) => `${size} (${count})`)
                  .join(", ")}
              </div>
            )}
          </div>

          <div className="flex justify-center">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:opacity-90 transition"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSizeButton = (type) => {
    const sizeField =
      type === "childAbove5"
        ? "childAbove5TshirtSize"
        : "childBelow5TshirtSize";
    const sizesStr = formData[sizeField];
    const sizes = stringToArray(sizesStr);
    const count = formData.accompany[type];

    if (count === 0) return null;

    // Calculate count of each size
    const sizeCounts = sizes.reduce((acc, size) => {
      acc[size] = (acc[size] || 0) + 1;
      return acc;
    }, {});

    return (
      <div className="ml-3">
        <button
          type="button"
          onClick={() =>
            type === "childAbove5"
              ? setShowChildAbove5TShirtModal(true)
              : setShowChildBelow5TShirtModal(true)
          }
          style={{
            padding: "6px 12px",
            backgroundColor: "rgba(255,255,255,0.2)",
            border: "1px solid rgba(255,255,255,0.3)",
            color: "#fff",
            borderRadius: "5px",
            fontSize: "14px",
            cursor: "pointer",
            width: "100%",
            textAlign: "left",
            minWidth: "100px",
          }}
          disabled={existingRegistration?.paymentStatus === "Paid"}
        >
          {sizes.length === 0 ? (
            `Select ${count} T-Shirt(s)`
          ) : (
            <div>
              <div>
                Sizes:{" "}
                {Object.entries(sizeCounts)
                  .map(([size, count]) => `${size}(${count})`)
                  .join(", ")}
              </div>
              <div style={{ fontSize: "10px", opacity: 0.8 }}>
                {sizes.length} of {count} selected
              </div>
            </div>
          )}
        </button>
      </div>
    );
  };

  const handleHomeClick = () => {
    navigate("/home");
  };

  // Updated accompany categories with proper labels and pricing
  const accompanyCategories = [
    { key: "spouse", label: "Spouse", showTshirt: false },
    { key: "childAbove5", label: "Children Above 5", showTshirt: true },
    { key: "childBelow5", label: "Children Below 5", showTshirt: true },
    { key: "guest", label: "Guest", showTshirt: false },
    { key: "driver", label: "Driver", showTshirt: false },
  ];

  return (
    <div className="form-container px-4">
      <h2 className="form-title m-0">Event Registration</h2>
      <h5
        style={{ textAlign: "center", color: "#58db8c", marginBottom: "20px" }}
      >
        KGC'97 Day 2026
      </h5>

      {showSuccess && (
        <div className="success-message">
          🎉 Registration {existingRegistration ? "updated" : "submitted"}{" "}
          successfully!
        </div>
      )}

      {/* Mobile Search Section */}
      {!showForm && (
        <div className="search-section mb-6">
          <div className="text-center mb-2">
            <p className="text-gray-300 text-white text-lg">
              Enter your 11 digits mobile number
            </p>
          </div>

          <div className="row justify-content-center">
            <div className="col-12 col-md-8 col-lg-6">
              <div className="input-group input-group-lg">
                <input
                  type="text"
                  className="form-control bg-light text-dark border-green-500"
                  placeholder="01XXXXXXXX"
                  value={searchMobile}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 11) {
                      setSearchMobile(value);
                      setSearchError("");
                    }
                  }}
                  maxLength="11"
                  style={{ height: "50px" }}
                />
                <button
                  className="btn btn-search d-flex align-items-center gap-2"
                  onClick={handleSearchRegistration}
                  disabled={searchLoading || !searchMobile.trim()}
                  style={{ height: "50px", width: "60px", padding: "0 15px" }}
                  title="Search"
                >
                  {searchLoading ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <ArrowRight size={24} />
                  )}
                </button>
              </div>
              {searchError && (
                <div className="text-danger small mt-2 text-center">
                  {searchError}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Show form after search */}
      {showForm && (
        <form onSubmit={(e) => e.preventDefault()}>
          {/* Existing Registration Notification */}
          {existingRegistration && (
            <div className="alert alert-info mb-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <strong>✓ Registration Found</strong>
                  <div className="small">
                    {existingRegistration.paymentStatus === "Paid"
                      ? "This registration is already paid. View details below."
                      : "You can update this registration."}
                  </div>
                  {existingRegistration.paymentStatus === "Paid" && (
                    <div className="small text-warning mt-1">
                      Paid registrations cannot be modified
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Existing Directory Notification */}
          {existingDirectory && !existingRegistration && (
            <div className="alert alert-secondary mb-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <strong>📋 Directory Entry Found</strong>
                  <div className="small">
                    Found directory entry for this mobile. Some fields are
                    pre-filled.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* New Entry Notification */}
          {isNewEntry && !existingRegistration && !existingDirectory && (
            <div className="alert alert-warning mb-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <strong>➕ Create New Registration</strong>
                  <div className="small">
                    No existing record found for mobile: {originalMobile}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mobile Field - Fixed display */}
          <div className="form-group">
            <label className="form-label">Mobile Number*</label>
            <div className="mobile-display">
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#e9ecef",
                  color: "#495057",
                  borderRadius: "5px",
                  border: "1px solid #dee2e6",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                {originalMobile || "No mobile number"}
              </div>
              <input type="hidden" name="mobile" value={originalMobile || ""} />
            </div>
            {errors.mobile && (
              <div className="error-message">{errors.mobile}</div>
            )}
          </div>

          {/* Name */}
          <div className="form-group">
            <label className="form-label">Name of Participant *</label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              disabled={existingRegistration?.paymentStatus === "Paid"}
              readOnly={existingRegistration?.paymentStatus === "Paid"}
              style={
                existingRegistration?.paymentStatus === "Paid"
                  ? { backgroundColor: "#e9ecef", color: "#495057" }
                  : {}
              }
            />
            {errors.name && <div className="error-message">{errors.name}</div>}
          </div>

          {/* Batch */}
          <div style={{ width: "100%", position: "relative" }}>
            <label className="form-label">Group </label>
            <select
              value={formData.batch}
              onChange={(e) => handleInputChange("batch", e.target.value)}
              disabled={existingRegistration?.paymentStatus === "Paid"}
              style={{
                width: "100%",
                padding: "8px 15px",
                borderRadius: "0px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                background:
                  existingRegistration?.paymentStatus === "Paid"
                    ? "#e9ecef"
                    : "rgba(255,255,255,0.1)",
                color:
                  existingRegistration?.paymentStatus === "Paid"
                    ? "#495057"
                    : "white",
                fontSize: "1rem",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                backdropFilter: "blur(8px)",
                transition: "all 0.3s ease",
                backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
                  existingRegistration?.paymentStatus === "Paid"
                    ? "#495057"
                    : "white"
                }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "1rem",
                marginBottom: "15px",
              }}
            >
              <option
                value=""
                style={{ backgroundColor: "#cffae0", color: "black" }}
              >
                Select your group
              </option>
              {Object.keys(batchPricing).map((batch) => (
                <option
                  key={batch}
                  value={batch}
                  style={{ backgroundColor: "#cffae0", color: "black" }}
                >
                  {batch}
                </option>
              ))}
            </select>
          </div>

          {/* Email */}
          <div className="form-group">
            <label className="form-label">Email </label>
            <input
              type="email"
              className="form-input"
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              disabled={existingRegistration?.paymentStatus === "Paid"}
              readOnly={existingRegistration?.paymentStatus === "Paid"}
              style={
                existingRegistration?.paymentStatus === "Paid"
                  ? { backgroundColor: "#e9ecef", color: "#495057" }
                  : {}
              }
            />
            {errors.email && (
              <div className="error-message">{errors.email}</div>
            )}
          </div>

          {/* Gender Selection */}
          <div className="form-group">
            <label className="form-label">Select Gender </label>
            <div
              style={{
                display: "flex",
                gap: "20px",
                color: "white",
                marginBottom: "15px",
              }}
            >
              <label
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <input
                  type="radio"
                  name="gender"
                  value="Male"
                  checked={gender === "Male"}
                  onChange={(e) => setGender(e.target.value)}
                  disabled={existingRegistration?.paymentStatus === "Paid"}
                />
                Male
              </label>
              <label
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <input
                  type="radio"
                  name="gender"
                  value="Female"
                  checked={gender === "Female"}
                  onChange={(e) => setGender(e.target.value)}
                  disabled={existingRegistration?.paymentStatus === "Paid"}
                />
                Female
              </label>
            </div>
          </div>

          {/* T-shirt Size */}
          <div style={{ width: "100%", position: "relative" }}>
            <label className="form-label">T-Shirt Size </label>
            <select
              value={formData.tshirtSize}
              onChange={(e) => handleInputChange("tshirtSize", e.target.value)}
              disabled={existingRegistration?.paymentStatus === "Paid"}
              style={{
                width: "100%",
                padding: "8px 15px",
                borderRadius: "0px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                background:
                  existingRegistration?.paymentStatus === "Paid"
                    ? "#e9ecef"
                    : "rgba(255,255,255,0.1)",
                color:
                  existingRegistration?.paymentStatus === "Paid"
                    ? "#495057"
                    : "white",
                fontSize: "1rem",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                backdropFilter: "blur(8px)",
                transition: "all 0.3s ease",
                backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
                  existingRegistration?.paymentStatus === "Paid"
                    ? "#495057"
                    : "white"
                }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "1rem",
                marginBottom: "15px",
              }}
            >
              <option
                value=""
                style={{ backgroundColor: "#cffae0", color: "black" }}
              >
                Select your T-Shirt Size
              </option>
              {tShirtSize.map((size) => (
                <option
                  key={size}
                  value={size}
                  style={{ backgroundColor: "#cffae0", color: "black" }}
                >
                  {size}
                </option>
              ))}
            </select>
            {errors.tshirtSize && (
              <div className="error-message">{errors.tshirtSize}</div>
            )}
          </div>

          {/* Accompany Section - UPDATED */}
          <div className="form-group">
            <label className="form-label">Accompanying Persons</label>
            <div
              className="accompany-section"
              style={{
                padding: "15px",
                borderRadius: "12px",
                background: "rgba(255,255,255,0.2)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                opacity:
                  existingRegistration?.paymentStatus === "Paid" ? 0.6 : 1,
              }}
            >
              {accompanyCategories.map((category) => (
                <div key={category.key}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "15px",
                      color: "#fff",
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ flex: 1 }}>
                      {category.label} (
                      {(() => {
                        const amount =
                          batchPricing["Arts"]?.[
                            category.key === "spouse"
                              ? "spouseFee"
                              : category.key === "guest"
                              ? "guestFee"
                              : category.key === "driver"
                              ? "driverFee"
                              : category.key === "childAbove5"
                              ? "above5Fee"
                              : "below5Fee"
                          ] || 0;

                        return amount > 0 ? `Tk. ${amount}` : "Free";
                      })()}
                      )
                    </span>

                    {/* <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <button
                        className="btn"
                        style={{
                          backgroundColor: "rgba(255,255,255,0.2)",
                          border: "1px solid rgba(255,255,255,0.3)",
                          color: "#fff",
                          width: "30px",
                          height: "30px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        type="button"
                        onClick={() => handleAccompanyChange(category.key, -1)}
                        disabled={
                          existingRegistration?.paymentStatus === "Paid"
                        }
                      >
                        -
                      </button>
                      <span style={{ width: "40px", textAlign: "center" }}>
                        {formData.accompany[category.key]}
                      </span>
                      <button
                        className="btn"
                        style={{
                          backgroundColor: "rgba(255,255,255,0.2)",
                          border: "1px solid rgba(255,255,255,0.3)",
                          color: "#fff",
                          width: "30px",
                          height: "30px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        type="button"
                        onClick={() => handleAccompanyChange(category.key, 1)}
                        disabled={
                          existingRegistration?.paymentStatus === "Paid"
                        }
                      >
                        +
                      </button>

                      {category.showTshirt && renderSizeButton(category.key)}
                    </div> */}

<div className="d-flex flex-column flex-md-row align-items-center justify-content-between ">
  <div className="d-flex align-items-center gap-2">
    <button
      className="btn"
      style={{
        backgroundColor: "rgba(255,255,255,0.2)",
        border: "1px solid rgba(255,255,255,0.3)",
        color: "#fff",
        width: "30px",
        height: "30px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      type="button"
      onClick={() => handleAccompanyChange(category.key, -1)}
      disabled={existingRegistration?.paymentStatus === "Paid"}
    >
      -
    </button>
    <span style={{ width: "40px", textAlign: "center" }}>
      {formData.accompany[category.key]}
    </span>
    <button
      className="btn"
      style={{
        backgroundColor: "rgba(255,255,255,0.2)",
        border: "1px solid rgba(255,255,255,0.3)",
        color: "#fff",
        width: "30px",
        height: "30px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      type="button"
      onClick={() => handleAccompanyChange(category.key, 1)}
      disabled={existingRegistration?.paymentStatus === "Paid"}
    >
      +
    </button>
  </div>
  
  {/* Only show T-shirt size button for children categories */}
  {category.showTshirt && (
    <div className="w-100 w-md-auto mt-2 mt-md-0">
      {renderSizeButton(category.key)}
    </div>
  )}
</div>
                  </div>

                  {/* Display validation errors for children T-shirt sizes */}
                  {formData.accompany[category.key] > 0 &&
                    category.showTshirt && (
                      <div style={{ marginBottom: "10px" }}>
                        {category.key === "childAbove5" &&
                          errors.childAbove5TshirtSize && (
                            <div
                              className="error-message"
                              style={{
                                color: "#ff6b6b",
                                marginTop: "5px",
                                fontSize: "12px",
                              }}
                            >
                              {errors.childAbove5TshirtSize}
                            </div>
                          )}
                        {category.key === "childBelow5" &&
                          errors.childBelow5TshirtSize && (
                            <div
                              className="error-message"
                              style={{
                                color: "#ff6b6b",
                                marginTop: "5px",
                                fontSize: "12px",
                              }}
                            >
                              {errors.childBelow5TshirtSize}
                            </div>
                          )}
                      </div>
                    )}
                </div>
              ))}
            </div>
          </div>

          {/* T-Shirt Size Modals */}
          <TShirtSizeModal
            isOpen={showChildAbove5TShirtModal}
            onClose={() => setShowChildAbove5TShirtModal(false)}
            onSelect={(size, action) =>
              handleTShirtSizeModalSelect("childAbove5TshirtSize", size, action)
            }
            selectedSizesStr={formData.childAbove5TshirtSize}
            title="Select T-Shirt Sizes for Child Above 5"
            maxSelections={formData.accompany.childAbove5}
            availableSizes={childAbove5TShirtSize}
            onClear={() => handleClearSelection("childAbove5TshirtSize")}
          />

          <TShirtSizeModal
            isOpen={showChildBelow5TShirtModal}
            onClose={() => setShowChildBelow5TShirtModal(false)}
            onSelect={(size, action) =>
              handleTShirtSizeModalSelect("childBelow5TshirtSize", size, action)
            }
            selectedSizesStr={formData.childBelow5TshirtSize}
            title="Select T-Shirt Sizes for Child Below 5"
            maxSelections={formData.accompany.childBelow5}
            availableSizes={childBelow5TShirtSize}
            onClear={() => handleClearSelection("childBelow5TshirtSize")}
          />

          {/* Total */}
          {/* <div
            className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "1.5rem",
              marginTop: "20px",
            }}
          >
            <span>Total amount:</span>
            <span className="amount-title">৳{calculateTotal()}</span>
          </div> */}
          {/* Total */}
<div
  className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "1.5rem",
    marginTop: "20px",
  }}
>
  <span>Total amount:</span>
  <span className="amount-title">৳{calculateTotal()}</span>
</div>

{/* Additional Payment Section */}
{showAdditionalPayment && existingRegistration?.paymentStatus === "Success" && (
  <div
    className="mt-4 p-4 bg-yellow-100 border border-yellow-400 rounded-xl"
    style={{
      background: "rgba(255, 193, 7, 0.1)",
      border: "1px solid #ffc107",
    }}
  >
    <div className="text-yellow-300 font-bold mb-2">
      ⚠️ Additional Payment Required
    </div>
    <div className="text-white mb-3">
      You have added more accompanying persons. Please pay the additional amount.
    </div>
    <div
      className="flex justify-between items-center text-white mb-3 p-2 bg-black/20 rounded"
    >
      <span>Originally Paid:</span>
      <span className="font-bold">৳{existingRegistration.totalAmount || 0}</span>
    </div>
    <div
      className="flex justify-between items-center text-white mb-3 p-2 bg-black/20 rounded"
    >
      <span>New Total:</span>
      <span className="font-bold">৳{calculateTotal()}</span>
    </div>
    <div
      className="flex justify-between items-center text-yellow-300 font-bold text-lg mb-4 p-2 bg-black/30 rounded"
    >
      <span>Additional Amount to Pay:</span>
      <span>৳{additionalPaymentAmount}</span>
    </div>
    <button
      type="button"
      className="w-full py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold rounded-xl hover:opacity-90 transition disabled:opacity-50"
      onClick={handleAdditionalPayment}
      disabled={additionalPaymentLoading}
    >
      {additionalPaymentLoading ? (
        <>
          <span className="spinner-border spinner-border-sm me-2" />
          Processing...
        </>
      ) : (
        `Pay Additional ৳${additionalPaymentAmount}`
      )}
    </button>
  </div>
)}

          {/* Buttons */}
          <div className="button-group flex gap-4 mt-6">
            {/* <button
              type="button"
              className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-white text-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSave}
              disabled={
                loading || existingRegistration?.paymentStatus === "Success"
              }
            >
              {loading ? (
                <>
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  {existingRegistration ? "Updating..." : "Processing..."}
                </>
              ) : existingRegistration ? (
                existingRegistration.paymentStatus === "Success" ? (
                  "Already Paid"
                ) : (
                  "Submit & Pay Now"
                )
              ) : (
                "Submit & Pay Now"
              )}
            </button> */}
            <button
  type="button"
  className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-white text-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
  onClick={handleSave}
  disabled={
    loading || 
    existingRegistration?.paymentStatus === "Success" || 
    additionalPaymentLoading
  }
>
  {loading || additionalPaymentLoading ? (
    <>
      <div className="spinner-border spinner-border-sm" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      Processing...
    </>
  ) : existingRegistration?.paymentStatus === "Success" && showAdditionalPayment ? (
    "Proceed to Additional Payment"
  ) : existingRegistration?.paymentStatus === "Success" ? (
    "Already Paid - View Only"
  ) : existingRegistration ? (
    existingRegistration.paymentStatus === "Paid" ? (
      "Already Paid"
    ) : (
      "Submit & Pay Now"
    )
  ) : (
    "Submit & Pay Now"
  )}
</button>

            <button
              type="button"
              className="flex justify-center items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
              onClick={handleHomeClick}
            >
              <Home size={18} />
              Go To Home
            </button>
          </div>
        </form>
      )}

      {/* Show only home button if not showing form */}
      {!showForm && (
        <div className="flex justify-center text-center mt-4">
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
            onClick={handleHomeClick}
          >
            <Home size={18} />
            Go To Home
          </button>
        </div>
      )}

      {/* Payment Info */}
      {showForm && (
        <>
          <div className="d-flex justify-content-center mt-4">
            <img
              src={`${process.env.PUBLIC_URL}/images/payment.jpg`}
              alt="Payment"
              className="img-fluid h-25 object-fit-cover rounded-2"
            />
          </div>

          <div className="hero-text mt-8 flex justify-center items-center">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              style={{ fontSize: "16px", color: "#fff" }}
            >
              পেমেন্ট সংক্রান্ত টেকনিক্যাল সমস্যায় যোগাযোগ করুন : 01711065316
            </motion.p>
          </div>
        </>
      )}
    </div>
  );
};

export default RegistrationPage;

// import { motion } from "framer-motion";
// import { ArrowRight, Home, Minus, Plus, X } from "lucide-react";
// import React, { useEffect, useState } from "react";
// import { useNavigate, useParams, useSearchParams } from "react-router-dom";
// import { useAuth } from "../context/AuthContext";
// import api from "../services/api";

// const RegistrationPage = () => {
//   const { id } = useParams();
//   const navigate = useNavigate();

//   // Search state
//   const [searchMobile, setSearchMobile] = useState("");
//   const [searchLoading, setSearchLoading] = useState(false);
//   const [searchError, setSearchError] = useState("");
//   const [existingRegistration, setExistingRegistration] = useState(null);
//   const [existingDirectory, setExistingDirectory] = useState(null);
//   const [showForm, setShowForm] = useState(false);
//   const [isNewEntry, setIsNewEntry] = useState(false);

//   // Form data state - UPDATED ACCOMPANY STRUCTURE
//   const [formData, setFormData] = useState({
//     name: "",
//     batch: "",
//     mobile: "",
//     email: "",
//     tshirtSize: "",
//     accompany: {
//       spouse: 0,
//       guest: 0,
//       driver: 0,
//       childAbove5: 0,
//       childBelow5: 0,
//     },
//     childAbove5TshirtSize: "",
//     childBelow5TshirtSize: "",
//   });

//   const [errors, setErrors] = useState({});
//   const [showSuccess, setShowSuccess] = useState(false);
//   const [batchPricing, setBatchPricing] = useState({});
//   const [tShirtSize, setTShirtSize] = useState([]);
//   const [childAbove5TShirtSize, setChildAbove5TShirtSize] = useState([]);
//   const [childBelow5TShirtSize, setChildBelow5TShirtSize] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [gender, setGender] = useState("Male");
//   const [showChildAbove5TShirtModal, setShowChildAbove5TShirtModal] =
//     useState(false);
//   const [showChildBelow5TShirtModal, setShowChildBelow5TShirtModal] =
//     useState(false);

//   const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;
//   const { user, getAuthHeader } = useAuth();

//   // Store original mobile from search
//   const [originalMobile, setOriginalMobile] = useState("");

//   const stringToArray = (str) => {
//     if (!str) return [];
//     return str
//       .split(",")
//       .map((s) => s.trim())
//       .filter((s) => s);
//   };

//   const arrayToString = (arr) => {
//     if (!arr || arr.length === 0) return "";
//     return arr.join(",");
//   };

//   // Fetch batch pricing on mount
//   useEffect(() => {
//     const fetchBatchPricing = async () => {
//       try {
//         const data = await api.batchPricing.getAll();
//         const pricingMap = {};
//         data.forEach((bp) => {
//           pricingMap[bp.batchName] = {
//             participantFee: bp.participantFee,
//             spouseFee: bp.spouseFee || 0,
//             guestFee: bp.guestFee || 0,
//             driverFee: bp.driverFee || 0,
//             above5Fee: bp.above5Fee || 0,
//             below5Fee: bp.below5Fee || 0,
//           };
//         });
//         setBatchPricing(pricingMap);
//       } catch (err) {
//         console.error("Error fetching batch pricing:", err);
//       }
//     };

//     fetchBatchPricing();
//   }, []);

//   // Fetch T-shirt sizes when gender changes
//   useEffect(() => {
//     if (!gender) return;

//     const fetchTShirtSizes = async () => {
//       try {
//         const data = await api.lookup.getTShirtByGender(gender);
//         const tShirtSizes = data.map((item) => item.name);
//         setTShirtSize(tShirtSizes);
//       } catch (err) {
//         console.error("Error fetching t-shirt sizes:", err);
//       }
//     };

//     fetchTShirtSizes();
//   }, [gender]);

//   // Fetch T-shirt sizes for children above 5
//   useEffect(() => {
//     const fetchChildAbove5TShirtSizes = async () => {
//       try {
//         const data = await api.lookup.getTShirtByGender("Child>5");
//         const tShirtSizes = data.map((item) => item.name);
//         setChildAbove5TShirtSize(tShirtSizes);
//       } catch (err) {
//         console.error(
//           "Error fetching t-shirt sizes for children above 5:",
//           err
//         );
//       }
//     };

//     fetchChildAbove5TShirtSizes();
//   }, []);

//   // Fetch T-shirt sizes for children below 5
//   useEffect(() => {
//     const fetchChildBelow5TShirtSizes = async () => {
//       try {
//         const data = await api.lookup.getTShirtByGender("Child<5");
//         const tShirtSizes = data.map((item) => item.name);
//         setChildBelow5TShirtSize(tShirtSizes);
//       } catch (err) {
//         console.error(
//           "Error fetching t-shirt sizes for children below 5:",
//           err
//         );
//       }
//     };

//     fetchChildBelow5TShirtSizes();
//   }, []);

//   const [searchParams] = useSearchParams();

//   // Handle mobile parameter from URL
//   useEffect(() => {
//     const mobileParam = searchParams.get("mobile");
//     if (mobileParam) {
//       setSearchMobile(mobileParam);

//       if (mobileParam.length === 11) {
//         handleSearchRegistrationWithMobile(mobileParam);
//       } else {
//         setSearchError("Mobile number must be 11 digits");
//       }
//     }
//   }, [searchParams]);

//   // Search function with mobile parameter
//   const handleSearchRegistrationWithMobile = async (mobileNumber) => {
//     console.log("Searching for mobile:", mobileNumber);

//     if (!mobileNumber || mobileNumber.length < 11) {
//       setSearchError("Please enter a valid 11-digit mobile number");
//       return;
//     }

//     try {
//       setSearchLoading(true);
//       setSearchError("");
//       setExistingRegistration(null);
//       setExistingDirectory(null);
//       setIsNewEntry(false);

//       // First, search for existing registration
//       const registrationResponse = await api.registration.getMyRegistrations(
//         mobileNumber
//       );

//       if (registrationResponse && registrationResponse.length > 0) {
//         // Found existing registration
//         const registration = registrationResponse[0];
//         setExistingRegistration(registration);

//         // Auto-fill form with registration data - UPDATED FOR NEW STRUCTURE
//         setFormData({
//           name: registration.name || "",
//           batch: registration.batch || "",
//           mobile: registration.mobile || mobileNumber,
//           email: registration.email || "",
//           tshirtSize: registration.tshirtSize || "",
//           accompany: {
//             spouse: registration.accompany?.spouse || 0,
//             guest: registration.accompany?.guest || 0,
//             driver: registration.accompany?.driver || 0,
//             childAbove5: registration.accompany?.childAbove5 || 0,
//             childBelow5: registration.accompany?.childBelow5 || 0,
//           },
//           childAbove5TshirtSize: registration.childAbove5TshirtSize || "",
//           childBelow5TshirtSize: registration.childBelow5TshirtSize || "",
//         });

//         setOriginalMobile(registration.mobile || mobileNumber);
//         setGender(registration.gender || "Male");

//         setShowForm(true);
//         setIsNewEntry(false);

//         // Check payment status
//         if (registration.paymentStatus === "Paid") {
//           setSearchError(
//             "This registration is already paid and cannot be modified."
//           );
//         }

//         return;
//       }

//       // If no registration found, search directory
//       const directoryResponse = await api.directory.searchByMobile(
//         mobileNumber
//       );

//       if (directoryResponse && directoryResponse.length > 0) {
//         // Found existing directory - use as base for new registration
//         const directory = directoryResponse[0];
//         setExistingDirectory(directory);

//         // Auto-fill form with directory data - UPDATED FOR NEW STRUCTURE
//         setFormData({
//           name: directory.name || "",
//           batch: directory.batch || "",
//           mobile: directory.mobile || mobileNumber,
//           email: directory.email || "",
//           tshirtSize: "",
//           accompany: {
//             spouse: 0,
//             guest: 0,
//             driver: 0,
//             childAbove5: 0,
//             childBelow5: 0,
//           },
//           childAbove5TshirtSize: "",
//           childBelow5TshirtSize: "",
//         });

//         setOriginalMobile(directory.mobile || mobileNumber);
//         setShowForm(true);
//         setIsNewEntry(true);
//       } else {
//         // No existing record found - start fresh
//         setFormData({
//           name: "",
//           batch: "",
//           mobile: mobileNumber,
//           email: "",
//           tshirtSize: "",
//           accompany: {
//             spouse: 0,
//             guest: 0,
//             driver: 0,
//             childAbove5: 0,
//             childBelow5: 0,
//           },
//           childAbove5TshirtSize: "",
//           childBelow5TshirtSize: "",
//         });

//         setOriginalMobile(mobileNumber);
//         setShowForm(true);
//         setIsNewEntry(true);
//       }
//     } catch (error) {
//       console.error("Error searching:", error);
//       setSearchError("Failed to search. Please try again.");
//     } finally {
//       setSearchLoading(false);
//     }
//   };

//   // Original search function
//   const handleSearchRegistration = async () => {
//     await handleSearchRegistrationWithMobile(searchMobile);
//   };

//   const handleResetSearch = () => {
//     setSearchMobile("");
//     setExistingRegistration(null);
//     setExistingDirectory(null);
//     setShowForm(false);
//     setIsNewEntry(false);
//     setFormData({
//       name: "",
//       batch: "",
//       mobile: "",
//       email: "",
//       tshirtSize: "",
//       accompany: {
//         spouse: 0,
//         guest: 0,
//         driver: 0,
//         childAbove5: 0,
//         childBelow5: 0,
//       },
//       childAbove5TshirtSize: "",
//       childBelow5TshirtSize: "",
//     });
//     setOriginalMobile("");
//     setGender("Male");
//     setSearchError("");
//   };

//   // Ensure mobile field is always set to originalMobile
//   useEffect(() => {
//     if (originalMobile) {
//       setFormData((prev) => ({
//         ...prev,
//         mobile: originalMobile,
//       }));
//     }
//   }, [originalMobile]);

//   const handleInputChange = (field, value) => {
//     if (field === "mobile") {
//       return; // Don't allow mobile to be changed
//     }

//     setFormData((prev) => ({ ...prev, [field]: value }));

//     if (errors[field]) {
//       setErrors((prev) => ({ ...prev, [field]: "" }));
//     }
//   };

//   const handleAccompanyChange = (type, inc) => {
//     const newCount = Math.max(0, formData.accompany[type] + inc);

//     setFormData((prev) => {
//       // Only child categories have t-shirt size options
//       let sizeField = "";
//       if (type === "childAbove5") {
//         sizeField = "childAbove5TshirtSize";
//       } else if (type === "childBelow5") {
//         sizeField = "childBelow5TshirtSize";
//       }

//       if (sizeField) {
//         const currentSizes = stringToArray(prev[sizeField]);
//         let updatedSizes = currentSizes;

//         if (newCount < prev.accompany[type]) {
//           // Decreasing count - remove last size
//           updatedSizes = currentSizes.slice(0, newCount);
//         } else if (newCount === 0) {
//           updatedSizes = [];
//         }

//         return {
//           ...prev,
//           accompany: {
//             ...prev.accompany,
//             [type]: newCount,
//           },
//           [sizeField]: arrayToString(updatedSizes),
//         };
//       } else {
//         // For spouse, guest, driver - no t-shirt size selection needed
//         return {
//           ...prev,
//           accompany: {
//             ...prev.accompany,
//             [type]: newCount,
//           },
//         };
//       }
//     });
//   };

//   const handleTShirtSizeModalSelect = (type, size, action) => {
//     const currentSizes = stringToArray(formData[type]);
//     const maxSelections =
//       formData.accompany[
//         type === "childAbove5TshirtSize" ? "childAbove5" : "childBelow5"
//       ];

//     // Count occurrences of the selected size
//     const sizeCount = currentSizes.filter((s) => s === size).length;

//     let newSizes = [...currentSizes];

//     if (action === "increase") {
//       // Increase count if we haven't reached max selections
//       if (currentSizes.length < maxSelections) {
//         newSizes.push(size);
//       }
//     } else if (action === "decrease") {
//       // Decrease count if this size exists
//       if (sizeCount > 0) {
//         const indexToRemove = newSizes.lastIndexOf(size);
//         if (indexToRemove > -1) {
//           newSizes.splice(indexToRemove, 1);
//         }
//       }
//     } else if (action === "remove") {
//       // Remove all occurrences of this size
//       newSizes = newSizes.filter((s) => s !== size);
//     }

//     setFormData((prev) => ({
//       ...prev,
//       [type]: arrayToString(newSizes),
//     }));
//   };

//   const handleClearSelection = (type) => {
//     setFormData((prev) => ({
//       ...prev,
//       [type]: "",
//     }));
//   };

//   const validateForm = () => {
//     const newErrors = {};
//     if (!formData.name.trim()) newErrors.name = "Name is required";

//     // Mobile validation - always use originalMobile
//     const mobileToValidate = originalMobile || formData.mobile;
//     if (!mobileToValidate || !mobileToValidate.trim()) {
//       newErrors.mobile = "Mobile number is required";
//     } else if (!/^\d{11}$/.test(mobileToValidate)) {
//       newErrors.mobile = "Must be 11 digits";
//     }

//     // Validate child above 5 t-shirt sizes
//     const childAbove5Sizes = stringToArray(formData.childAbove5TshirtSize);
//     if (
//       formData.accompany.childAbove5 > 0 &&
//       childAbove5Sizes.length !== formData.accompany.childAbove5
//     ) {
//       newErrors.childAbove5TshirtSize = `Please select ${formData.accompany.childAbove5} t-shirt size(s) for ${formData.accompany.childAbove5} child(ren) above 5`;
//     }

//     // Validate child below 5 t-shirt sizes
//     const childBelow5Sizes = stringToArray(formData.childBelow5TshirtSize);
//     if (
//       formData.accompany.childBelow5 > 0 &&
//       childBelow5Sizes.length !== formData.accompany.childBelow5
//     ) {
//       newErrors.childBelow5TshirtSize = `Please select ${formData.accompany.childBelow5} t-shirt size(s) for ${formData.accompany.childBelow5} child(ren) below 5`;
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const calculateTotal = () => {
//     const batch = batchPricing["Arts"];
//     if (!batch) return 0;

//     return (
//       batch.participantFee +
//       formData.accompany.spouse * (batch.spouseFee || 0) +
//       formData.accompany.guest * (batch.guestFee || 0) +
//       formData.accompany.driver * (batch.driverFee || 0) +
//       formData.accompany.childAbove5 * (batch.above5Fee || 0) +
//       formData.accompany.childBelow5 * (batch.below5Fee || 0)
//     );
//   };

//   const handleSubmit = async () => {
//     const mobileToUse = originalMobile || formData.mobile;

//     if (!validateForm()) {
//       console.log("Validation failed with errors:", errors);
//       return;
//     }

//     if (!formData.name || !formData.name.trim()) {
//       alert("Please enter a name");
//       return;
//     }

//     if (!mobileToUse || !/^\d{11}$/.test(mobileToUse)) {
//       alert("Please ensure mobile number is 11 digits");
//       return;
//     }

//     setLoading(true);
//     try {
//       const formDataToSend = new FormData();
//       formDataToSend.append("name", formData.name);
//       formDataToSend.append("batch", formData.batch);
//       formDataToSend.append("mobile", mobileToUse);
//       formDataToSend.append("email", formData.email);
//       formDataToSend.append("tshirtSize", formData.tshirtSize);
//       formDataToSend.append("gender", gender);
//       formDataToSend.append("accompanySpouse", formData.accompany.spouse);
//       formDataToSend.append("accompanyGuest", formData.accompany.guest);
//       formDataToSend.append("accompanyDriver", formData.accompany.driver);
//       formDataToSend.append(
//         "accompanyChildAbove5",
//         formData.accompany.childAbove5
//       );
//       formDataToSend.append(
//         "accompanyChildBelow5",
//         formData.accompany.childBelow5
//       );
//       formDataToSend.append(
//         "childAbove5TshirtSize",
//         formData.childAbove5TshirtSize || ""
//       );
//       formDataToSend.append(
//         "childBelow5TshirtSize",
//         formData.childBelow5TshirtSize || ""
//       );

//       if (user?.id) {
//         formDataToSend.append("userId", user.id.toString());
//       }

//       let data;
//       if (existingRegistration && existingRegistration.id) {
//         // Update existing registration
//         data = await api.registration.update(
//           existingRegistration.id,
//           formDataToSend
//         );
//       } else {
//         // Create new registration
//         data = await api.registration.create(formDataToSend);
//       }

//       setShowSuccess(true);
//       setTimeout(() => setShowSuccess(false), 3000);

//       return data;
//     } catch (error) {
//       console.error("Error submitting registration:", error);
//       alert(
//         `Registration ${existingRegistration ? "update" : "failed"}: ${
//           error.message
//         }`
//       );
//       return false;
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handlePayment = async (participantData) => {
//     try {
//       const payload = {
//         name: formData.name,
//         batch: formData.batch,
//         mobile: originalMobile || formData.mobile,
//         email: formData.email,
//         tshirtSize: formData.tshirtSize,
//         accompanySpouse: formData.accompany.spouse,
//         accompanyGuest: formData.accompany.guest,
//         accompanyDriver: formData.accompany.driver,
//         accompanyChildAbove5: formData.accompany.childAbove5,
//         accompanyChildBelow5: formData.accompany.childBelow5,
//         totalAmount: calculateTotal(),
//         order_id: participantData?.orderId || `order_${Date.now()}`,
//         participantId: participantData?.id || existingRegistration?.id,
//         userId: user?.id,
//         childAbove5TshirtSize: formData.childAbove5TshirtSize,
//         childBelow5TshirtSize: formData.childBelow5TshirtSize,
//       };

//       const data = await api.payment.create(payload);

//       if (data.checkout_url) {
//         window.location.href = data.checkout_url;
//       } else {
//         alert(data.error || "Failed to get checkout URL");
//       }
//     } catch (error) {
//       console.error("Payment request failed", error);
//       alert("Payment request failed. Try again.");
//     }
//   };

//   const handleSave = async () => {
//     if (existingRegistration && existingRegistration.paymentStatus === "Paid") {
//       alert("This registration is already paid and cannot be modified.");
//       return;
//     }

//     const submitted = await handleSubmit();
//     if (submitted) {
//       await handlePayment(submitted);
//     }
//   };

//   const TShirtSizeModal = ({
//     isOpen,
//     onClose,
//     onSelect,
//     selectedSizesStr,
//     title,
//     maxSelections,
//     availableSizes,
//     onClear,
//   }) => {
//     if (!isOpen) return null;

//     const selectedSizes = stringToArray(selectedSizesStr);

//     // Calculate count of each selected size
//     const sizeCounts = selectedSizes.reduce((acc, size) => {
//       acc[size] = (acc[size] || 0) + 1;
//       return acc;
//     }, {});

//     const totalSelected = selectedSizes.length;
//     const remainingToSelect = maxSelections - totalSelected;

//     return (
//       <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-42">
//         <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-6 w-full max-w-md relative">
//           {/* Close button - Top right */}
//           <button
//             onClick={onClose}
//             className="absolute top-4 right-4 text-red-500 hover:text-gray-700 transition-colors"
//             aria-label="Close modal"
//           >
//             <X size={24} />
//           </button>

//           <h3 className="text-xl font-bold text-gray-800 mb-2 pr-8">{title}</h3>
//           <div className="mb-4">
//             <p className="text-gray-600">
//               Select <span className="font-bold">{maxSelections}</span> -
//               Remaining: <span className="font-bold">{remainingToSelect}</span>
//             </p>
//             <div>
//               <span>
//                 (Click <span className="font-medium text-green-600">+</span> to
//                 add a size and 
//                 <span className="font-medium text-red-600"> -</span> to remove a
//                 size)
//               </span>
//             </div>
//           </div>

//           {/* Clear button */}
//           {selectedSizes.length > 0 && (
//             <div className="mb-4 flex justify-end">
//               <button
//                 onClick={onClear}
//                 className="px-3 py-1 text-sm bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
//               >
//                 Clear All
//               </button>
//             </div>
//           )}

//           <div className="grid grid-cols-2 gap-3 mb-6">
//             {availableSizes.map((size) => {
//               const selectedCount = sizeCounts[size] || 0;
//               const canIncrease = remainingToSelect > 0;
//               const canDecrease = selectedCount > 0;

//               return (
//                 <div
//                   key={size}
//                   className={`p-3 rounded-xl font-medium transition-all ${
//                     selectedCount > 0
//                       ? "bg-gradient-to-br from-green-500 to-blue-500 text-white shadow-lg"
//                       : "bg-white text-gray-700 border border-gray-200"
//                   }`}
//                 >
//                   <div className="flex items-center justify-between">
//                     <div className="flex items-center gap-3">
//                       <span className="font-bold text-lg">{size}</span>
//                       {selectedCount > 0 && (
//                         <span className="text-white font-bold">
//                           × {selectedCount}
//                         </span>
//                       )}
//                     </div>

//                     <div className="flex items-center gap-2">
//                       {/* Decrease Button */}
//                       <button
//                         onClick={() => onSelect(size, "decrease")}
//                         disabled={!canDecrease}
//                         className={`p-1 rounded-full ${
//                           canDecrease
//                             ? "bg-red-100 text-red-600 hover:bg-red-200"
//                             : "bg-gray-100 text-gray-400 cursor-not-allowed"
//                         }`}
//                         title="Decrease"
//                       >
//                         <Minus size={18} />
//                       </button>

//                       {/* Increase Button */}
//                       <button
//                         onClick={() => onSelect(size, "increase")}
//                         disabled={!canIncrease}
//                         className={`p-1 rounded-full ${
//                           canIncrease
//                             ? "bg-green-100 text-green-600 hover:bg-green-200"
//                             : "bg-gray-100 text-gray-400 cursor-not-allowed"
//                         }`}
//                         title="Increase"
//                       >
//                         <Plus size={18} />
//                       </button>
//                     </div>
//                   </div>
//                 </div>
//               );
//             })}
//           </div>

//           <div className="flex justify-between items-center mb-6">
//             <div className="text-sm text-gray-600">
//               Selected: <span className="font-bold">{totalSelected}</span>/
//               {maxSelections}
//             </div>
//             {selectedSizes.length > 0 && (
//               <div className="text-sm text-gray-700 max-w-[60%]">
//                 <span className="font-medium">Summary:</span>{" "}
//                 {Object.entries(sizeCounts)
//                   .map(([size, count]) => `${size} (${count})`)
//                   .join(", ")}
//               </div>
//             )}
//           </div>

//           <div className="flex justify-center">
//             <button
//               onClick={onClose}
//               className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:opacity-90 transition"
//             >
//               Done
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   const renderSizeButton = (type) => {
//     const sizeField =
//       type === "childAbove5"
//         ? "childAbove5TshirtSize"
//         : "childBelow5TshirtSize";
//     const sizesStr = formData[sizeField];
//     const sizes = stringToArray(sizesStr);
//     const count = formData.accompany[type];

//     if (count === 0) return null;

//     // Calculate count of each size
//     const sizeCounts = sizes.reduce((acc, size) => {
//       acc[size] = (acc[size] || 0) + 1;
//       return acc;
//     }, {});

//     return (
//       <div className="ml-3">
//         <button
//           type="button"
//           onClick={() =>
//             type === "childAbove5"
//               ? setShowChildAbove5TShirtModal(true)
//               : setShowChildBelow5TShirtModal(true)
//           }
//           style={{
//             padding: "6px 12px",
//             backgroundColor: "rgba(255,255,255,0.2)",
//             border: "1px solid rgba(255,255,255,0.3)",
//             color: "#fff",
//             borderRadius: "5px",
//             fontSize: "14px",
//             cursor: "pointer",
//             width: "100%",
//             textAlign: "left",
//             minWidth: "100px",
//           }}
//           disabled={existingRegistration?.paymentStatus === "Paid"}
//         >
//           {sizes.length === 0 ? (
//             `Select ${count} T-Shirt(s)`
//           ) : (
//             <div>
//               <div>
//                 Sizes:{" "}
//                 {Object.entries(sizeCounts)
//                   .map(([size, count]) => `${size}(${count})`)
//                   .join(", ")}
//               </div>
//               <div style={{ fontSize: "10px", opacity: 0.8 }}>
//                 {sizes.length} of {count} selected
//               </div>
//             </div>
//           )}
//         </button>
//       </div>
//     );
//   };

//   const handleHomeClick = () => {
//     navigate("/home");
//   };

//   // Updated accompany categories with proper labels and pricing
//   const accompanyCategories = [
//     { key: "spouse", label: "Spouse", showTshirt: false },
//     { key: "childAbove5", label: "Children Above 5", showTshirt: true },
//     { key: "childBelow5", label: "Children Below 5", showTshirt: true },
//     { key: "guest", label: "Guest", showTshirt: false },
//     { key: "driver", label: "Driver", showTshirt: false },
//   ];

//   return (
//     <div className="form-container px-4">
//       <h2 className="form-title m-0">Event Registration</h2>
//       <h5
//         style={{ textAlign: "center", color: "#58db8c", marginBottom: "20px" }}
//       >
//         KGC'97 Day 2026
//       </h5>

//       {showSuccess && (
//         <div className="success-message">
//           🎉 Registration {existingRegistration ? "updated" : "submitted"}{" "}
//           successfully!
//         </div>
//       )}

//       {/* Mobile Search Section */}
//       {!showForm && (
//         <div className="search-section mb-6">
//           <div className="text-center mb-2">
//             <p className="text-gray-300 text-white text-lg">
//               Enter your 11 digits mobile number
//             </p>
//           </div>

//           <div className="row justify-content-center">
//             <div className="col-12 col-md-8 col-lg-6">
//               <div className="input-group input-group-lg">
//                 <input
//                   type="text"
//                   className="form-control bg-light text-dark border-green-500"
//                   placeholder="01XXXXXXXX"
//                   value={searchMobile}
//                   onChange={(e) => {
//                     const value = e.target.value.replace(/\D/g, "");
//                     if (value.length <= 11) {
//                       setSearchMobile(value);
//                       setSearchError("");
//                     }
//                   }}
//                   maxLength="11"
//                   style={{ height: "50px" }}
//                 />
//                 <button
//                   className="btn btn-search d-flex align-items-center gap-2"
//                   onClick={handleSearchRegistration}
//                   disabled={searchLoading || !searchMobile.trim()}
//                   style={{ height: "50px", width: "60px", padding: "0 15px" }}
//                   title="Search"
//                 >
//                   {searchLoading ? (
//                     <span className="spinner-border spinner-border-sm" />
//                   ) : (
//                     <ArrowRight size={24} />
//                   )}
//                 </button>
//               </div>
//               {searchError && (
//                 <div className="text-danger small mt-2 text-center">
//                   {searchError}
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Show form after search */}
//       {showForm && (
//         <form onSubmit={(e) => e.preventDefault()}>
//           {/* Existing Registration Notification */}
//           {existingRegistration && (
//             <div className="alert alert-info mb-4">
//               <div className="d-flex align-items-center justify-content-between">
//                 <div>
//                   <strong>✓ Registration Found</strong>
//                   <div className="small">
//                     {existingRegistration.paymentStatus === "Paid"
//                       ? "This registration is already paid. View details below."
//                       : "You can update this registration."}
//                   </div>
//                   {existingRegistration.paymentStatus === "Paid" && (
//                     <div className="small text-warning mt-1">
//                       Paid registrations cannot be modified
//                     </div>
//                   )}
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* Existing Directory Notification */}
//           {existingDirectory && !existingRegistration && (
//             <div className="alert alert-secondary mb-4">
//               <div className="d-flex align-items-center justify-content-between">
//                 <div>
//                   <strong>📋 Directory Entry Found</strong>
//                   <div className="small">
//                     Found directory entry for this mobile. Some fields are
//                     pre-filled.
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* New Entry Notification */}
//           {isNewEntry && !existingRegistration && !existingDirectory && (
//             <div className="alert alert-warning mb-4">
//               <div className="d-flex align-items-center justify-content-between">
//                 <div>
//                   <strong>➕ Create New Registration</strong>
//                   <div className="small">
//                     No existing record found for mobile: {originalMobile}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* Mobile Field - Fixed display */}
//           <div className="form-group">
//             <label className="form-label">Mobile Number*</label>
//             <div className="mobile-display">
//               <div
//                 style={{
//                   padding: "12px",
//                   backgroundColor: "#e9ecef",
//                   color: "#495057",
//                   borderRadius: "5px",
//                   border: "1px solid #dee2e6",
//                   fontSize: "16px",
//                   fontWeight: "bold",
//                 }}
//               >
//                 {originalMobile || "No mobile number"}
//               </div>
//               <input type="hidden" name="mobile" value={originalMobile || ""} />
//             </div>
//             {errors.mobile && (
//               <div className="error-message">{errors.mobile}</div>
//             )}
//           </div>

//           {/* Name */}
//           <div className="form-group">
//             <label className="form-label">Name of Participant *</label>
//             <input
//               type="text"
//               className="form-input"
//               placeholder="Enter your full name"
//               value={formData.name}
//               onChange={(e) => handleInputChange("name", e.target.value)}
//               disabled={existingRegistration?.paymentStatus === "Paid"}
//               readOnly={existingRegistration?.paymentStatus === "Paid"}
//               style={
//                 existingRegistration?.paymentStatus === "Paid"
//                   ? { backgroundColor: "#e9ecef", color: "#495057" }
//                   : {}
//               }
//             />
//             {errors.name && <div className="error-message">{errors.name}</div>}
//           </div>

//           {/* Batch */}
//           <div style={{ width: "100%", position: "relative" }}>
//             <label className="form-label">Group </label>
//             <select
//               value={formData.batch}
//               onChange={(e) => handleInputChange("batch", e.target.value)}
//               disabled={existingRegistration?.paymentStatus === "Paid"}
//               style={{
//                 width: "100%",
//                 padding: "8px 15px",
//                 borderRadius: "0px",
//                 border: "1px solid rgba(255, 255, 255, 0.2)",
//                 background:
//                   existingRegistration?.paymentStatus === "Paid"
//                     ? "#e9ecef"
//                     : "rgba(255,255,255,0.1)",
//                 color:
//                   existingRegistration?.paymentStatus === "Paid"
//                     ? "#495057"
//                     : "white",
//                 fontSize: "1rem",
//                 appearance: "none",
//                 WebkitAppearance: "none",
//                 MozAppearance: "none",
//                 backdropFilter: "blur(8px)",
//                 transition: "all 0.3s ease",
//                 backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
//                   existingRegistration?.paymentStatus === "Paid"
//                     ? "#495057"
//                     : "white"
//                 }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
//                 backgroundRepeat: "no-repeat",
//                 backgroundPosition: "right 12px center",
//                 backgroundSize: "1rem",
//                 marginBottom: "15px",
//               }}
//             >
//               <option
//                 value=""
//                 style={{ backgroundColor: "#cffae0", color: "black" }}
//               >
//                 Select your group
//               </option>
//               {Object.keys(batchPricing).map((batch) => (
//                 <option
//                   key={batch}
//                   value={batch}
//                   style={{ backgroundColor: "#cffae0", color: "black" }}
//                 >
//                   {batch}
//                 </option>
//               ))}
//             </select>
//           </div>

//           {/* Email */}
//           <div className="form-group">
//             <label className="form-label">Email </label>
//             <input
//               type="email"
//               className="form-input"
//               placeholder="your.email@example.com"
//               value={formData.email}
//               onChange={(e) => handleInputChange("email", e.target.value)}
//               disabled={existingRegistration?.paymentStatus === "Paid"}
//               readOnly={existingRegistration?.paymentStatus === "Paid"}
//               style={
//                 existingRegistration?.paymentStatus === "Paid"
//                   ? { backgroundColor: "#e9ecef", color: "#495057" }
//                   : {}
//               }
//             />
//             {errors.email && (
//               <div className="error-message">{errors.email}</div>
//             )}
//           </div>

//           {/* Gender Selection */}
//           <div className="form-group">
//             <label className="form-label">Select Gender </label>
//             <div
//               style={{
//                 display: "flex",
//                 gap: "20px",
//                 color: "white",
//                 marginBottom: "15px",
//               }}
//             >
//               <label
//                 style={{ display: "flex", alignItems: "center", gap: "5px" }}
//               >
//                 <input
//                   type="radio"
//                   name="gender"
//                   value="Male"
//                   checked={gender === "Male"}
//                   onChange={(e) => setGender(e.target.value)}
//                   disabled={existingRegistration?.paymentStatus === "Paid"}
//                 />
//                 Male
//               </label>
//               <label
//                 style={{ display: "flex", alignItems: "center", gap: "5px" }}
//               >
//                 <input
//                   type="radio"
//                   name="gender"
//                   value="Female"
//                   checked={gender === "Female"}
//                   onChange={(e) => setGender(e.target.value)}
//                   disabled={existingRegistration?.paymentStatus === "Paid"}
//                 />
//                 Female
//               </label>
//             </div>
//           </div>

//           {/* T-shirt Size */}
//           <div style={{ width: "100%", position: "relative" }}>
//             <label className="form-label">T-Shirt Size </label>
//             <select
//               value={formData.tshirtSize}
//               onChange={(e) => handleInputChange("tshirtSize", e.target.value)}
//               disabled={existingRegistration?.paymentStatus === "Paid"}
//               style={{
//                 width: "100%",
//                 padding: "8px 15px",
//                 borderRadius: "0px",
//                 border: "1px solid rgba(255, 255, 255, 0.2)",
//                 background:
//                   existingRegistration?.paymentStatus === "Paid"
//                     ? "#e9ecef"
//                     : "rgba(255,255,255,0.1)",
//                 color:
//                   existingRegistration?.paymentStatus === "Paid"
//                     ? "#495057"
//                     : "white",
//                 fontSize: "1rem",
//                 appearance: "none",
//                 WebkitAppearance: "none",
//                 MozAppearance: "none",
//                 backdropFilter: "blur(8px)",
//                 transition: "all 0.3s ease",
//                 backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
//                   existingRegistration?.paymentStatus === "Paid"
//                     ? "#495057"
//                     : "white"
//                 }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
//                 backgroundRepeat: "no-repeat",
//                 backgroundPosition: "right 12px center",
//                 backgroundSize: "1rem",
//                 marginBottom: "15px",
//               }}
//             >
//               <option
//                 value=""
//                 style={{ backgroundColor: "#cffae0", color: "black" }}
//               >
//                 Select your T-Shirt Size
//               </option>
//               {tShirtSize.map((size) => (
//                 <option
//                   key={size}
//                   value={size}
//                   style={{ backgroundColor: "#cffae0", color: "black" }}
//                 >
//                   {size}
//                 </option>
//               ))}
//             </select>
//             {errors.tshirtSize && (
//               <div className="error-message">{errors.tshirtSize}</div>
//             )}
//           </div>

//           {/* Accompany Section - UPDATED */}
//           <div className="form-group">
//             <label className="form-label">Accompanying Persons</label>
//             <div
//               className="accompany-section"
//               style={{
//                 padding: "15px",
//                 borderRadius: "12px",
//                 background: "rgba(255,255,255,0.2)",
//                 border: "1px solid rgba(255, 255, 255, 0.2)",
//                 opacity:
//                   existingRegistration?.paymentStatus === "Paid" ? 0.6 : 1,
//               }}
//             >
//               {accompanyCategories.map((category) => (
//                 <div key={category.key}>
//                   <div
//                     style={{
//                       display: "flex",
//                       alignItems: "center",
//                       justifyContent: "space-between",
//                       marginBottom: "15px",
//                       color: "#fff",
//                       flexWrap: "wrap",
//                     }}
//                   >
//                     <span style={{ flex: 1 }}>
//                       {category.label} (
//                       {(() => {
//                         const amount =
//                           batchPricing["Arts"]?.[
//                             category.key === "spouse"
//                               ? "spouseFee"
//                               : category.key === "guest"
//                               ? "guestFee"
//                               : category.key === "driver"
//                               ? "driverFee"
//                               : category.key === "childAbove5"
//                               ? "above5Fee"
//                               : "below5Fee"
//                           ] || 0;

//                         return amount > 0 ? `Tk. ${amount}` : "Free";
//                       })()}
//                       )
//                     </span>

//                     {/* <div
//                       style={{
//                         display: "flex",
//                         alignItems: "center",
//                         gap: "10px",
//                       }}
//                     >
//                       <button
//                         className="btn"
//                         style={{
//                           backgroundColor: "rgba(255,255,255,0.2)",
//                           border: "1px solid rgba(255,255,255,0.3)",
//                           color: "#fff",
//                           width: "30px",
//                           height: "30px",
//                           display: "flex",
//                           alignItems: "center",
//                           justifyContent: "center",
//                         }}
//                         type="button"
//                         onClick={() => handleAccompanyChange(category.key, -1)}
//                         disabled={
//                           existingRegistration?.paymentStatus === "Paid"
//                         }
//                       >
//                         -
//                       </button>
//                       <span style={{ width: "40px", textAlign: "center" }}>
//                         {formData.accompany[category.key]}
//                       </span>
//                       <button
//                         className="btn"
//                         style={{
//                           backgroundColor: "rgba(255,255,255,0.2)",
//                           border: "1px solid rgba(255,255,255,0.3)",
//                           color: "#fff",
//                           width: "30px",
//                           height: "30px",
//                           display: "flex",
//                           alignItems: "center",
//                           justifyContent: "center",
//                         }}
//                         type="button"
//                         onClick={() => handleAccompanyChange(category.key, 1)}
//                         disabled={
//                           existingRegistration?.paymentStatus === "Paid"
//                         }
//                       >
//                         +
//                       </button>

//                       {category.showTshirt && renderSizeButton(category.key)}
//                     </div> */}

// <div className="d-flex flex-column flex-md-row align-items-center justify-content-between ">
//   <div className="d-flex align-items-center gap-2">
//     <button
//       className="btn"
//       style={{
//         backgroundColor: "rgba(255,255,255,0.2)",
//         border: "1px solid rgba(255,255,255,0.3)",
//         color: "#fff",
//         width: "30px",
//         height: "30px",
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//       }}
//       type="button"
//       onClick={() => handleAccompanyChange(category.key, -1)}
//       disabled={existingRegistration?.paymentStatus === "Paid"}
//     >
//       -
//     </button>
//     <span style={{ width: "40px", textAlign: "center" }}>
//       {formData.accompany[category.key]}
//     </span>
//     <button
//       className="btn"
//       style={{
//         backgroundColor: "rgba(255,255,255,0.2)",
//         border: "1px solid rgba(255,255,255,0.3)",
//         color: "#fff",
//         width: "30px",
//         height: "30px",
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//       }}
//       type="button"
//       onClick={() => handleAccompanyChange(category.key, 1)}
//       disabled={existingRegistration?.paymentStatus === "Paid"}
//     >
//       +
//     </button>
//   </div>
  
//   {/* Only show T-shirt size button for children categories */}
//   {category.showTshirt && (
//     <div className="w-100 w-md-auto mt-2 mt-md-0">
//       {renderSizeButton(category.key)}
//     </div>
//   )}
// </div>
//                   </div>

//                   {/* Display validation errors for children T-shirt sizes */}
//                   {formData.accompany[category.key] > 0 &&
//                     category.showTshirt && (
//                       <div style={{ marginBottom: "10px" }}>
//                         {category.key === "childAbove5" &&
//                           errors.childAbove5TshirtSize && (
//                             <div
//                               className="error-message"
//                               style={{
//                                 color: "#ff6b6b",
//                                 marginTop: "5px",
//                                 fontSize: "12px",
//                               }}
//                             >
//                               {errors.childAbove5TshirtSize}
//                             </div>
//                           )}
//                         {category.key === "childBelow5" &&
//                           errors.childBelow5TshirtSize && (
//                             <div
//                               className="error-message"
//                               style={{
//                                 color: "#ff6b6b",
//                                 marginTop: "5px",
//                                 fontSize: "12px",
//                               }}
//                             >
//                               {errors.childBelow5TshirtSize}
//                             </div>
//                           )}
//                       </div>
//                     )}
//                 </div>
//               ))}
//             </div>
//           </div>

//           {/* T-Shirt Size Modals */}
//           <TShirtSizeModal
//             isOpen={showChildAbove5TShirtModal}
//             onClose={() => setShowChildAbove5TShirtModal(false)}
//             onSelect={(size, action) =>
//               handleTShirtSizeModalSelect("childAbove5TshirtSize", size, action)
//             }
//             selectedSizesStr={formData.childAbove5TshirtSize}
//             title="Select T-Shirt Sizes for Child Above 5"
//             maxSelections={formData.accompany.childAbove5}
//             availableSizes={childAbove5TShirtSize}
//             onClear={() => handleClearSelection("childAbove5TshirtSize")}
//           />

//           <TShirtSizeModal
//             isOpen={showChildBelow5TShirtModal}
//             onClose={() => setShowChildBelow5TShirtModal(false)}
//             onSelect={(size, action) =>
//               handleTShirtSizeModalSelect("childBelow5TshirtSize", size, action)
//             }
//             selectedSizesStr={formData.childBelow5TshirtSize}
//             title="Select T-Shirt Sizes for Child Below 5"
//             maxSelections={formData.accompany.childBelow5}
//             availableSizes={childBelow5TShirtSize}
//             onClear={() => handleClearSelection("childBelow5TshirtSize")}
//           />

//           {/* Total */}
//           <div
//             className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
//             style={{
//               display: "flex",
//               justifyContent: "space-between",
//               alignItems: "center",
//               fontSize: "1.5rem",
//               marginTop: "20px",
//             }}
//           >
//             <span>Total amount:</span>
//             <span className="amount-title">৳{calculateTotal()}</span>
//           </div>

//           {/* Buttons */}
//           <div className="button-group flex gap-4 mt-6">
//             <button
//               type="button"
//               className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-white text-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
//               onClick={handleSave}
//               disabled={
//                 loading || existingRegistration?.paymentStatus === "Success"
//               }
//             >
//               {loading ? (
//                 <>
//                   <div
//                     className="spinner-border spinner-border-sm"
//                     role="status"
//                   >
//                     <span className="visually-hidden">Loading...</span>
//                   </div>
//                   {existingRegistration ? "Updating..." : "Processing..."}
//                 </>
//               ) : existingRegistration ? (
//                 existingRegistration.paymentStatus === "Success" ? (
//                   "Already Paid"
//                 ) : (
//                   "Submit & Pay Now"
//                 )
//               ) : (
//                 "Submit & Pay Now"
//               )}
//             </button>

//             <button
//               type="button"
//               className="flex justify-center items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
//               onClick={handleHomeClick}
//             >
//               <Home size={18} />
//               Go To Home
//             </button>
//           </div>
//         </form>
//       )}

//       {/* Show only home button if not showing form */}
//       {!showForm && (
//         <div className="flex justify-center text-center mt-4">
//           <button
//             type="button"
//             className="flex items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
//             onClick={handleHomeClick}
//           >
//             <Home size={18} />
//             Go To Home
//           </button>
//         </div>
//       )}

//       {/* Payment Info */}
//       {showForm && (
//         <>
//           <div className="d-flex justify-content-center mt-4">
//             <img
//               src={`${process.env.PUBLIC_URL}/images/payment.jpg`}
//               alt="Payment"
//               className="img-fluid h-25 object-fit-cover rounded-2"
//             />
//           </div>

//           <div className="hero-text mt-8 flex justify-center items-center">
//             <motion.p
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               transition={{ delay: 0.5 }}
//               style={{ fontSize: "16px", color: "#fff" }}
//             >
//               পেমেন্ট সংক্রান্ত টেকনিক্যাল সমস্যায় যোগাযোগ করুন : 01711065316
//             </motion.p>
//           </div>
//         </>
//       )}
//     </div>
//   );
// };

// export default RegistrationPage;

// import { motion } from "framer-motion";
// import { ArrowRight, Home, X } from "lucide-react";
// import React, { useEffect, useState } from "react";
// import { useNavigate, useParams, useSearchParams } from "react-router-dom";
// import { useAuth } from "../context/AuthContext";
// import api from "../services/api";

// const RegistrationPage = () => {
//   const { id } = useParams();
//   const navigate = useNavigate();

//   // Search state
//   const [searchMobile, setSearchMobile] = useState("");
//   const [searchLoading, setSearchLoading] = useState(false);
//   const [searchError, setSearchError] = useState("");
//   const [existingRegistration, setExistingRegistration] = useState(null);
//   const [existingDirectory, setExistingDirectory] = useState(null);
//   const [showForm, setShowForm] = useState(false);
//   const [isNewEntry, setIsNewEntry] = useState(false);

//   // Form data state - UPDATED ACCOMPANY STRUCTURE
//   const [formData, setFormData] = useState({
//     name: "",
//     batch: "",
//     mobile: "",
//     email: "",
//     tshirtSize: "",
//     accompany: {
//       spouse: 0,
//       guest: 0,
//       driver: 0,
//       childAbove5: 0,
//       childBelow5: 0,
//     },
//     childAbove5TshirtSize: "",
//     childBelow5TshirtSize: "",
//   });

//   const [errors, setErrors] = useState({});
//   const [showSuccess, setShowSuccess] = useState(false);
//   const [batchPricing, setBatchPricing] = useState({});
//   const [tShirtSize, setTShirtSize] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [gender, setGender] = useState("Male");
//   const [showChildAbove5TShirtModal, setShowChildAbove5TShirtModal] =
//     useState(false);
//   const [showChildBelow5TShirtModal, setShowChildBelow5TShirtModal] =
//     useState(false);

//   const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;
//   const { user, getAuthHeader } = useAuth();

//   // Store original mobile from search
//   const [originalMobile, setOriginalMobile] = useState("");

//   const stringToArray = (str) => {
//     if (!str) return [];
//     return str
//       .split(",")
//       .map((s) => s.trim())
//       .filter((s) => s);
//   };

//   const arrayToString = (arr) => {
//     if (!arr || arr.length === 0) return "";
//     return arr.join(",");
//   };

//   // Fetch batch pricing on mount
//   useEffect(() => {
//     const fetchBatchPricing = async () => {
//       try {
//         const data = await api.batchPricing.getAll();
//         const pricingMap = {};
//         data.forEach((bp) => {
//           pricingMap[bp.batchName] = {
//             participantFee: bp.participantFee,
//             spouseFee: bp.spouseFee || 0,
//             guestFee: bp.guestFee || 0,
//             driverFee: bp.driverFee || 0,
//             above5Fee: bp.above5Fee || 0,
//             below5Fee: bp.below5Fee || 0,
//           };
//         });
//         setBatchPricing(pricingMap);
//       } catch (err) {
//         console.error("Error fetching batch pricing:", err);
//       }
//     };

//     fetchBatchPricing();
//   }, []);

//   // Fetch T-shirt sizes when gender changes
//   useEffect(() => {
//     if (!gender) return;

//     const fetchTShirtSizes = async () => {
//       try {
//         const data = await api.lookup.getTShirtByGender(gender);
//         const tShirtSizes = data.map((item) => item.name);
//         setTShirtSize(tShirtSizes);
//       } catch (err) {
//         console.error("Error fetching t-shirt sizes:", err);
//       }
//     };

//     fetchTShirtSizes();
//   }, [gender]);

//   const [searchParams] = useSearchParams();

//   // Handle mobile parameter from URL
//   useEffect(() => {
//     const mobileParam = searchParams.get("mobile");
//     if (mobileParam) {
//       setSearchMobile(mobileParam);

//       if (mobileParam.length === 11) {
//         handleSearchRegistrationWithMobile(mobileParam);
//       } else {
//         setSearchError("Mobile number must be 11 digits");
//       }
//     }
//   }, [searchParams]);

//   // Search function with mobile parameter
//   const handleSearchRegistrationWithMobile = async (mobileNumber) => {
//     console.log("Searching for mobile:", mobileNumber);

//     if (!mobileNumber || mobileNumber.length < 11) {
//       setSearchError("Please enter a valid 11-digit mobile number");
//       return;
//     }

//     try {
//       setSearchLoading(true);
//       setSearchError("");
//       setExistingRegistration(null);
//       setExistingDirectory(null);
//       setIsNewEntry(false);

//       // First, search for existing registration
//       const registrationResponse = await api.registration.getMyRegistrations(
//         mobileNumber
//       );

//       if (registrationResponse && registrationResponse.length > 0) {
//         // Found existing registration
//         const registration = registrationResponse[0];
//         setExistingRegistration(registration);

//         // Auto-fill form with registration data - UPDATED FOR NEW STRUCTURE
//         setFormData({
//           name: registration.name || "",
//           batch: registration.batch || "",
//           mobile: registration.mobile || mobileNumber,
//           email: registration.email || "",
//           tshirtSize: registration.tshirtSize || "",
//           accompany: {
//             spouse: registration.accompany?.spouse || 0,
//             guest: registration.accompany?.guest || 0,
//             driver: registration.accompany?.driver || 0,
//             childAbove5: registration.accompany?.childAbove5 || 0,
//             childBelow5: registration.accompany?.childBelow5 || 0,
//           },
//           childAbove5TshirtSize: registration.childAbove5TshirtSize || "",
//           childBelow5TshirtSize: registration.childBelow5TshirtSize || "",
//         });

//         setOriginalMobile(registration.mobile || mobileNumber);
//         setGender(registration.gender || "Male");

//         setShowForm(true);
//         setIsNewEntry(false);

//         // Check payment status
//         if (registration.paymentStatus === "Paid") {
//           setSearchError(
//             "This registration is already paid and cannot be modified."
//           );
//         }

//         return;
//       }

//       // If no registration found, search directory
//       const directoryResponse = await api.directory.searchByMobile(
//         mobileNumber
//       );

//       if (directoryResponse && directoryResponse.length > 0) {
//         // Found existing directory - use as base for new registration
//         const directory = directoryResponse[0];
//         setExistingDirectory(directory);

//         // Auto-fill form with directory data - UPDATED FOR NEW STRUCTURE
//         setFormData({
//           name: directory.name || "",
//           batch: directory.batch || "",
//           mobile: directory.mobile || mobileNumber,
//           email: directory.email || "",
//           tshirtSize: "",
//           accompany: {
//             spouse: 0,
//             guest: 0,
//             driver: 0,
//             childAbove5: 0,
//             childBelow5: 0,
//           },
//           childAbove5TshirtSize: "",
//           childBelow5TshirtSize: "",
//         });

//         setOriginalMobile(directory.mobile || mobileNumber);
//         setShowForm(true);
//         setIsNewEntry(true);
//       } else {
//         // No existing record found - start fresh
//         setFormData({
//           name: "",
//           batch: "",
//           mobile: mobileNumber,
//           email: "",
//           tshirtSize: "",
//           accompany: {
//             spouse: 0,
//             guest: 0,
//             driver: 0,
//             childAbove5: 0,
//             childBelow5: 0,
//           },
//           childAbove5TshirtSize: "",
//           childBelow5TshirtSize: "",
//         });

//         setOriginalMobile(mobileNumber);
//         setShowForm(true);
//         setIsNewEntry(true);
//       }
//     } catch (error) {
//       console.error("Error searching:", error);
//       setSearchError("Failed to search. Please try again.");
//     } finally {
//       setSearchLoading(false);
//     }
//   };

//   // Original search function
//   const handleSearchRegistration = async () => {
//     await handleSearchRegistrationWithMobile(searchMobile);
//   };

//   const handleResetSearch = () => {
//     setSearchMobile("");
//     setExistingRegistration(null);
//     setExistingDirectory(null);
//     setShowForm(false);
//     setIsNewEntry(false);
//     setFormData({
//       name: "",
//       batch: "",
//       mobile: "",
//       email: "",
//       tshirtSize: "",
//       accompany: {
//         spouse: 0,
//         guest: 0,
//         driver: 0,
//         childAbove5: 0,
//         childBelow5: 0,
//       },
//       childAbove5TshirtSize: "",
//       childBelow5TshirtSize: "",
//     });
//     setOriginalMobile("");
//     setGender("Male");
//     setSearchError("");
//   };

//   // Ensure mobile field is always set to originalMobile
//   useEffect(() => {
//     if (originalMobile) {
//       setFormData((prev) => ({
//         ...prev,
//         mobile: originalMobile,
//       }));
//     }
//   }, [originalMobile]);

//   const handleInputChange = (field, value) => {
//     if (field === "mobile") {
//       return; // Don't allow mobile to be changed
//     }

//     setFormData((prev) => ({ ...prev, [field]: value }));

//     if (errors[field]) {
//       setErrors((prev) => ({ ...prev, [field]: "" }));
//     }
//   };

//   const handleAccompanyChange = (type, inc) => {
//     const newCount = Math.max(0, formData.accompany[type] + inc);

//     setFormData((prev) => {
//       // Only child categories have t-shirt size options
//       let sizeField = "";
//       if (type === "childAbove5") {
//         sizeField = "childAbove5TshirtSize";
//       } else if (type === "childBelow5") {
//         sizeField = "childBelow5TshirtSize";
//       }

//       if (sizeField) {
//         const currentSizes = stringToArray(prev[sizeField]);
//         let updatedSizes = currentSizes;

//         if (newCount < prev.accompany[type]) {
//           updatedSizes = currentSizes.slice(0, newCount);
//         } else if (newCount === 0) {
//           updatedSizes = [];
//         }

//         return {
//           ...prev,
//           accompany: {
//             ...prev.accompany,
//             [type]: newCount,
//           },
//           [sizeField]: arrayToString(updatedSizes),
//         };
//       } else {
//         // For spouse, guest, driver - no t-shirt size selection needed
//         return {
//           ...prev,
//           accompany: {
//             ...prev.accompany,
//             [type]: newCount,
//           },
//         };
//       }
//     });
//   };

//   const handleTShirtSizeModalSelect = (type, size) => {
//     const currentSizes = stringToArray(formData[type]);
//     const maxSelections =
//       formData.accompany[
//         type === "childAbove5TshirtSize" ? "childAbove5" : "childBelow5"
//       ];

//     if (currentSizes.includes(size)) {
//       const newSizes = currentSizes.filter((s) => s !== size);
//       setFormData((prev) => ({
//         ...prev,
//         [type]: arrayToString(newSizes),
//       }));
//     } else {
//       if (currentSizes.length < maxSelections) {
//         const newSizes = [...currentSizes, size];
//         setFormData((prev) => ({
//           ...prev,
//           [type]: arrayToString(newSizes),
//         }));
//       }
//     }
//   };

//   const validateForm = () => {
//     const newErrors = {};
//     if (!formData.name.trim()) newErrors.name = "Name is required";
//     // if (!formData.batch) newErrors.batch = "Batch selection required";

//     // Mobile validation - always use originalMobile
//     const mobileToValidate = originalMobile || formData.mobile;
//     if (!mobileToValidate || !mobileToValidate.trim()) {
//       newErrors.mobile = "Mobile number is required";
//     } else if (!/^\d{11}$/.test(mobileToValidate)) {
//       newErrors.mobile = "Must be 11 digits";
//     }

//     // if (!formData.email.trim()) newErrors.email = "Email required";
//     // if (!formData.tshirtSize) newErrors.tshirtSize = "T-shirt size required";

//     // Validate child above 5 t-shirt sizes
//     const childAbove5Sizes = stringToArray(formData.childAbove5TshirtSize);
//     if (
//       formData.accompany.childAbove5 > 0 &&
//       childAbove5Sizes.length !== formData.accompany.childAbove5
//     ) {
//       newErrors.childAbove5TshirtSize = `Please select ${formData.accompany.childAbove5} t-shirt size(s) for ${formData.accompany.childAbove5} child(ren) above 5`;
//     }

//     // Validate child below 5 t-shirt sizes
//     const childBelow5Sizes = stringToArray(formData.childBelow5TshirtSize);
//     if (
//       formData.accompany.childBelow5 > 0 &&
//       childBelow5Sizes.length !== formData.accompany.childBelow5
//     ) {
//       newErrors.childBelow5TshirtSize = `Please select ${formData.accompany.childBelow5} t-shirt size(s) for ${formData.accompany.childBelow5} child(ren) below 5`;
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   // const calculateTotal = () => {
//   //   const batch = batchPricing[formData.batch];
//   //   if (!batch) return 0;

//   //   return (
//   //     batch.participantFee +
//   //     formData.accompany.spouse * (batch.spouseFee || 0) +
//   //     formData.accompany.guest * (batch.guestFee || 0) +
//   //     formData.accompany.driver * (batch.driverFee || 0) +
//   //     formData.accompany.childAbove5 * (batch.above5Fee || 0) +
//   //     formData.accompany.childBelow5 * (batch.below5Fee || 0)
//   //   );
//   // };

//   const calculateTotal = () => {
//     const batch = batchPricing["Arts"];
//     // console.log(batch);
//     if (!batch) return 0;

//     return (
//       batch.participantFee +
//       formData.accompany.spouse * (batch.spouseFee || 0) +
//       formData.accompany.guest * (batch.guestFee || 0) +
//       formData.accompany.driver * (batch.driverFee || 0) +
//       formData.accompany.childAbove5 * (batch.above5Fee || 0) +
//       formData.accompany.childBelow5 * (batch.below5Fee || 0)
//     );
//   };
//   const handleSubmit = async () => {
//     const mobileToUse = originalMobile || formData.mobile;

//     if (!validateForm()) {
//       console.log("Validation failed with errors:", errors);
//       return;
//     }

//     if (!formData.name || !formData.name.trim()) {
//       alert("Please enter a name");
//       return;
//     }

//     if (!mobileToUse || !/^\d{11}$/.test(mobileToUse)) {
//       alert("Please ensure mobile number is 11 digits");
//       return;
//     }

//     setLoading(true);
//     try {
//       const formDataToSend = new FormData();
//       formDataToSend.append("name", formData.name);
//       formDataToSend.append("batch", formData.batch);
//       formDataToSend.append("mobile", mobileToUse);
//       formDataToSend.append("email", formData.email);
//       formDataToSend.append("tshirtSize", formData.tshirtSize);
//       formDataToSend.append("gender", gender);
//       formDataToSend.append("accompanySpouse", formData.accompany.spouse);
//       formDataToSend.append("accompanyGuest", formData.accompany.guest);
//       formDataToSend.append("accompanyDriver", formData.accompany.driver);
//       formDataToSend.append(
//         "accompanyChildAbove5",
//         formData.accompany.childAbove5
//       );
//       formDataToSend.append(
//         "accompanyChildBelow5",
//         formData.accompany.childBelow5
//       );
//       formDataToSend.append(
//         "childAbove5TshirtSize",
//         formData.childAbove5TshirtSize || ""
//       );
//       formDataToSend.append(
//         "childBelow5TshirtSize",
//         formData.childBelow5TshirtSize || ""
//       );

//       if (user?.id) {
//         formDataToSend.append("userId", user.id.toString());
//       }

//       let data;
//       if (existingRegistration && existingRegistration.id) {
//         // Update existing registration
//         data = await api.registration.update(
//           existingRegistration.id,
//           formDataToSend
//         );
//       } else {
//         // Create new registration
//         data = await api.registration.create(formDataToSend);
//       }

//       setShowSuccess(true);
//       setTimeout(() => setShowSuccess(false), 3000);

//       return data;
//     } catch (error) {
//       console.error("Error submitting registration:", error);
//       alert(
//         `Registration ${existingRegistration ? "update" : "failed"}: ${
//           error.message
//         }`
//       );
//       return false;
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handlePayment = async (participantData) => {
//     try {
//       const payload = {
//         name: formData.name,
//         batch: formData.batch,
//         mobile: originalMobile || formData.mobile,
//         email: formData.email,
//         tshirtSize: formData.tshirtSize,
//         accompanySpouse: formData.accompany.spouse,
//         accompanyGuest: formData.accompany.guest,
//         accompanyDriver: formData.accompany.driver,
//         accompanyChildAbove5: formData.accompany.childAbove5,
//         accompanyChildBelow5: formData.accompany.childBelow5,
//         totalAmount: calculateTotal(),
//         order_id: participantData?.orderId || `order_${Date.now()}`,
//         participantId: participantData?.id || existingRegistration?.id,
//         userId: user?.id,
//         childAbove5TshirtSize: formData.childAbove5TshirtSize,
//         childBelow5TshirtSize: formData.childBelow5TshirtSize,
//       };

//       const data = await api.payment.create(payload);

//       if (data.checkout_url) {
//         window.location.href = data.checkout_url;
//       } else {
//         alert(data.error || "Failed to get checkout URL");
//       }
//     } catch (error) {
//       console.error("Payment request failed", error);
//       alert("Payment request failed. Try again.");
//     }
//   };

//   const handleSave = async () => {
//     if (existingRegistration && existingRegistration.paymentStatus === "Paid") {
//       alert("This registration is already paid and cannot be modified.");
//       return;
//     }

//     const submitted = await handleSubmit();
//     if (submitted) {
//       await handlePayment(submitted);
//     }
//   };

//   const TShirtSizeModal = ({
//     isOpen,
//     onClose,
//     onSelect,
//     selectedSizesStr,
//     title,
//     maxSelections,
//   }) => {
//     if (!isOpen) return null;

//     const selectedSizes = stringToArray(selectedSizesStr);

//     return (
//       <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
//         <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-6 w-full max-w-md relative">
//           {/* Close button - Top right */}
//           <button
//             onClick={onClose}
//             className="absolute top-4 right-4 text-red-500 hover:text-gray-700 transition-colors"
//             aria-label="Close modal"
//           >
//             <X size={24} />
//           </button>

//           <h3 className="text-xl font-bold text-gray-800 mb-2 pr-8">{title}</h3>
//           <p className="text-gray-600 mb-4">
//             Select {maxSelections} size(s) for {maxSelections} child(ren)
//           </p>

//           <div className="grid grid-cols-2 gap-3 mb-6">
//             {tShirtSize.map((size) => {
//               const isSelected = selectedSizes.includes(size);
//               const isDisabled =
//                 !isSelected && selectedSizes.length >= maxSelections;

//               return (
//                 <button
//                   key={size}
//                   onClick={() => onSelect(size)}
//                   disabled={isDisabled}
//                   className={`p-4 rounded-xl font-medium transition-all ${
//                     isSelected
//                       ? "bg-gradient-to-br from-green-500 to-blue-500 text-white shadow-lg"
//                       : "bg-white text-gray-700 border border-gray-200 hover:border-blue-300"
//                   } ${
//                     isDisabled
//                       ? "opacity-50 cursor-not-allowed"
//                       : "hover:shadow-md"
//                   }`}
//                 >
//                   {size}
//                   {isSelected && <span className="text-white ml-2">✓</span>}
//                 </button>
//               );
//             })}
//           </div>

//           <div className="flex justify-between items-center mb-6">
//             <div className="text-sm text-gray-600">
//               Selected:{" "}
//               <span className="font-bold">{selectedSizes.length}</span>/
//               {maxSelections}
//             </div>
//             {selectedSizes.length > 0 && (
//               <div className="text-sm text-gray-700">
//                 <span className="font-medium">Sizes:</span>{" "}
//                 {selectedSizes.join(", ")}
//               </div>
//             )}
//           </div>

//           <div className="flex justify-center">
//             <button
//               onClick={onClose}
//               className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:opacity-90 transition"
//             >
//               Done
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   const renderSizeButton = (type) => {
//     const sizeField =
//       type === "childAbove5"
//         ? "childAbove5TshirtSize"
//         : "childBelow5TshirtSize";
//     const sizesStr = formData[sizeField];
//     const sizes = stringToArray(sizesStr);
//     const count = formData.accompany[type];

//     if (count === 0) return null;

//     return (
//       <div className="ml-3">
//         <button
//           type="button"
//           onClick={() =>
//             type === "childAbove5"
//               ? setShowChildAbove5TShirtModal(true)
//               : setShowChildBelow5TShirtModal(true)
//           }
//           style={{
//             padding: "6px 12px",
//             backgroundColor: "rgba(255,255,255,0.2)",
//             border: "1px solid rgba(255,255,255,0.3)",
//             color: "#fff",
//             borderRadius: "5px",
//             fontSize: "12px",
//             cursor: "pointer",
//             width: "100%",
//             textAlign: "left",
//           }}
//           disabled={existingRegistration?.paymentStatus === "Paid"}
//         >
//           {sizes.length === 0 ? (
//             `Select ${count} T-Shirt(s)`
//           ) : (
//             <div>
//               <div>Sizes: {sizes.join(", ")}</div>
//               <div style={{ fontSize: "10px", opacity: 0.8 }}>
//                 {sizes.length} of {count} selected
//               </div>
//             </div>
//           )}
//         </button>
//       </div>
//     );
//   };

//   const handleHomeClick = () => {
//     navigate("/home");
//   };

//   // Updated accompany categories with proper labels and pricing
//   const accompanyCategories = [
//     { key: "spouse", label: "Spouse", showTshirt: false },
//     { key: "childAbove5", label: "Children Above 5", showTshirt: true },
//     { key: "childBelow5", label: "Children Below 5", showTshirt: true },
//     { key: "guest", label: "Guest", showTshirt: false },
//     { key: "driver", label: "Driver", showTshirt: false },
//   ];

//   return (
//     <div className="form-container px-4">
//       <h2 className="form-title m-0">Event Registration</h2>
//       <h5
//         style={{ textAlign: "center", color: "#58db8c", marginBottom: "20px" }}
//       >
//         KGC'97 Day 2026
//       </h5>

//       {showSuccess && (
//         <div className="success-message">
//           🎉 Registration {existingRegistration ? "updated" : "submitted"}{" "}
//           successfully!
//         </div>
//       )}

//       {/* Mobile Search Section */}
//       {!showForm && (
//         <div className="search-section mb-6">
//           <div className="text-center mb-2">
//             <p className="text-gray-300 text-white text-lg">
//               Enter your 11 digits mobile number
//             </p>
//           </div>

//           <div className="row justify-content-center">
//             <div className="col-12 col-md-8 col-lg-6">
//               <div className="input-group input-group-lg">
//                 <input
//                   type="text"
//                   className="form-control bg-light text-dark border-green-500"
//                   placeholder="01XXXXXXXX"
//                   value={searchMobile}
//                   onChange={(e) => {
//                     const value = e.target.value.replace(/\D/g, "");
//                     if (value.length <= 11) {
//                       setSearchMobile(value);
//                       setSearchError("");
//                     }
//                   }}
//                   maxLength="11"
//                   style={{ height: "50px" }}
//                 />
//                 <button
//                   className="btn btn-search d-flex align-items-center gap-2"
//                   onClick={handleSearchRegistration}
//                   disabled={searchLoading || !searchMobile.trim()}
//                   style={{ height: "50px", width: "60px", padding: "0 15px" }}
//                   title="Search"
//                 >
//                   {searchLoading ? (
//                     <span className="spinner-border spinner-border-sm" />
//                   ) : (
//                     <ArrowRight size={24} />
//                   )}
//                 </button>
//               </div>
//               {searchError && (
//                 <div className="text-danger small mt-2 text-center">
//                   {searchError}
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Show form after search */}
//       {showForm && (
//         <form onSubmit={(e) => e.preventDefault()}>
//           {/* Existing Registration Notification */}
//           {existingRegistration && (
//             <div className="alert alert-info mb-4">
//               <div className="d-flex align-items-center justify-content-between">
//                 <div>
//                   <strong>✓ Registration Found</strong>
//                   <div className="small">
//                     {existingRegistration.paymentStatus === "Paid"
//                       ? "This registration is already paid. View details below."
//                       : "You can update this registration."}
//                   </div>
//                   {existingRegistration.paymentStatus === "Paid" && (
//                     <div className="small text-warning mt-1">
//                       Paid registrations cannot be modified
//                     </div>
//                   )}
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* Existing Directory Notification */}
//           {existingDirectory && !existingRegistration && (
//             <div className="alert alert-secondary mb-4">
//               <div className="d-flex align-items-center justify-content-between">
//                 <div>
//                   <strong>📋 Directory Entry Found</strong>
//                   <div className="small">
//                     Found directory entry for this mobile. Some fields are
//                     pre-filled.
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* New Entry Notification */}
//           {isNewEntry && !existingRegistration && !existingDirectory && (
//             <div className="alert alert-warning mb-4">
//               <div className="d-flex align-items-center justify-content-between">
//                 <div>
//                   <strong>➕ Create New Registration</strong>
//                   <div className="small">
//                     No existing record found for mobile: {originalMobile}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* Mobile Field - Fixed display */}
//           <div className="form-group">
//             <label className="form-label">Mobile Number*</label>
//             <div className="mobile-display">
//               <div
//                 style={{
//                   padding: "12px",
//                   backgroundColor: "#e9ecef",
//                   color: "#495057",
//                   borderRadius: "5px",
//                   border: "1px solid #dee2e6",
//                   fontSize: "16px",
//                   fontWeight: "bold",
//                 }}
//               >
//                 {originalMobile || "No mobile number"}
//               </div>
//               <input type="hidden" name="mobile" value={originalMobile || ""} />
//             </div>
//             {errors.mobile && (
//               <div className="error-message">{errors.mobile}</div>
//             )}
//           </div>

//           {/* Name */}
//           <div className="form-group">
//             <label className="form-label">Name of Participant *</label>
//             <input
//               type="text"
//               className="form-input"
//               placeholder="Enter your full name"
//               value={formData.name}
//               onChange={(e) => handleInputChange("name", e.target.value)}
//               disabled={existingRegistration?.paymentStatus === "Paid"}
//               readOnly={existingRegistration?.paymentStatus === "Paid"}
//               style={
//                 existingRegistration?.paymentStatus === "Paid"
//                   ? { backgroundColor: "#e9ecef", color: "#495057" }
//                   : {}
//               }
//             />
//             {errors.name && <div className="error-message">{errors.name}</div>}
//           </div>

//           {/* Batch */}
//           <div style={{ width: "100%", position: "relative" }}>
//             <label className="form-label">Group </label>
//             <select
//               value={formData.batch}
//               onChange={(e) => handleInputChange("batch", e.target.value)}
//               disabled={existingRegistration?.paymentStatus === "Paid"}
//               style={{
//                 width: "100%",
//                 padding: "8px 15px",
//                 borderRadius: "0px",
//                 border: "1px solid rgba(255, 255, 255, 0.2)",
//                 background:
//                   existingRegistration?.paymentStatus === "Paid"
//                     ? "#e9ecef"
//                     : "rgba(255,255,255,0.1)",
//                 color:
//                   existingRegistration?.paymentStatus === "Paid"
//                     ? "#495057"
//                     : "white",
//                 fontSize: "1rem",
//                 appearance: "none",
//                 WebkitAppearance: "none",
//                 MozAppearance: "none",
//                 backdropFilter: "blur(8px)",
//                 transition: "all 0.3s ease",
//                 backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
//                   existingRegistration?.paymentStatus === "Paid"
//                     ? "#495057"
//                     : "white"
//                 }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
//                 backgroundRepeat: "no-repeat",
//                 backgroundPosition: "right 12px center",
//                 backgroundSize: "1rem",
//                 marginBottom: "15px",
//               }}
//             >
//               <option
//                 value=""
//                 style={{ backgroundColor: "#cffae0", color: "black" }}
//               >
//                 Select your group
//               </option>
//               {Object.keys(batchPricing).map((batch) => (
//                 <option
//                   key={batch}
//                   value={batch}
//                   style={{ backgroundColor: "#cffae0", color: "black" }}
//                 >
//                   {batch}
//                 </option>
//               ))}
//             </select>
//           </div>

//           {/* Email */}
//           <div className="form-group">
//             <label className="form-label">Email </label>
//             <input
//               type="email"
//               className="form-input"
//               placeholder="your.email@example.com"
//               value={formData.email}
//               onChange={(e) => handleInputChange("email", e.target.value)}
//               disabled={existingRegistration?.paymentStatus === "Paid"}
//               readOnly={existingRegistration?.paymentStatus === "Paid"}
//               style={
//                 existingRegistration?.paymentStatus === "Paid"
//                   ? { backgroundColor: "#e9ecef", color: "#495057" }
//                   : {}
//               }
//             />
//             {errors.email && (
//               <div className="error-message">{errors.email}</div>
//             )}
//           </div>

//           {/* Gender Selection */}
//           <div className="form-group">
//             <label className="form-label">Select Gender </label>
//             <div
//               style={{
//                 display: "flex",
//                 gap: "20px",
//                 color: "white",
//                 marginBottom: "15px",
//               }}
//             >
//               <label
//                 style={{ display: "flex", alignItems: "center", gap: "5px" }}
//               >
//                 <input
//                   type="radio"
//                   name="gender"
//                   value="Male"
//                   checked={gender === "Male"}
//                   onChange={(e) => setGender(e.target.value)}
//                   disabled={existingRegistration?.paymentStatus === "Paid"}
//                 />
//                 Male
//               </label>
//               <label
//                 style={{ display: "flex", alignItems: "center", gap: "5px" }}
//               >
//                 <input
//                   type="radio"
//                   name="gender"
//                   value="Female"
//                   checked={gender === "Female"}
//                   onChange={(e) => setGender(e.target.value)}
//                   disabled={existingRegistration?.paymentStatus === "Paid"}
//                 />
//                 Female
//               </label>
//             </div>
//           </div>

//           {/* T-shirt Size */}
//           <div style={{ width: "100%", position: "relative" }}>
//             <label className="form-label">T-Shirt Size </label>
//             <select
//               value={formData.tshirtSize}
//               onChange={(e) => handleInputChange("tshirtSize", e.target.value)}
//               disabled={existingRegistration?.paymentStatus === "Paid"}
//               style={{
//                 width: "100%",
//                 padding: "8px 15px",
//                 borderRadius: "0px",
//                 border: "1px solid rgba(255, 255, 255, 0.2)",
//                 background:
//                   existingRegistration?.paymentStatus === "Paid"
//                     ? "#e9ecef"
//                     : "rgba(255,255,255,0.1)",
//                 color:
//                   existingRegistration?.paymentStatus === "Paid"
//                     ? "#495057"
//                     : "white",
//                 fontSize: "1rem",
//                 appearance: "none",
//                 WebkitAppearance: "none",
//                 MozAppearance: "none",
//                 backdropFilter: "blur(8px)",
//                 transition: "all 0.3s ease",
//                 backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
//                   existingRegistration?.paymentStatus === "Paid"
//                     ? "#495057"
//                     : "white"
//                 }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
//                 backgroundRepeat: "no-repeat",
//                 backgroundPosition: "right 12px center",
//                 backgroundSize: "1rem",
//                 marginBottom: "15px",
//               }}
//             >
//               <option
//                 value=""
//                 style={{ backgroundColor: "#cffae0", color: "black" }}
//               >
//                 Select your T-Shirt Size
//               </option>
//               {tShirtSize.map((size) => (
//                 <option
//                   key={size}
//                   value={size}
//                   style={{ backgroundColor: "#cffae0", color: "black" }}
//                 >
//                   {size}
//                 </option>
//               ))}
//             </select>
//             {errors.tshirtSize && (
//               <div className="error-message">{errors.tshirtSize}</div>
//             )}
//           </div>

//           {/* Accompany Section - UPDATED */}
//           <div className="form-group">
//             <label className="form-label">Accompanying Persons</label>
//             <div
//               className="accompany-section"
//               style={{
//                 padding: "15px",
//                 borderRadius: "12px",
//                 background: "rgba(255,255,255,0.2)",
//                 border: "1px solid rgba(255, 255, 255, 0.2)",
//                 opacity:
//                   existingRegistration?.paymentStatus === "Paid" ? 0.6 : 1,
//               }}
//             >
//               {accompanyCategories.map((category) => (
//                 <div key={category.key}>
//                   <div
//                     style={{
//                       display: "flex",
//                       alignItems: "center",
//                       justifyContent: "space-between",
//                       marginBottom: "15px",
//                       color: "#fff",
//                     }}
//                   >
//                     <span style={{ flex: 1 }}>
//                       {category.label} (
//                       {(() => {
//                         const amount =
//                           batchPricing["Arts"]?.[
//                             category.key === "spouse"
//                               ? "spouseFee"
//                               : category.key === "guest"
//                               ? "guestFee"
//                               : category.key === "driver"
//                               ? "driverFee"
//                               : category.key === "childAbove5"
//                               ? "above5Fee"
//                               : "below5Fee"
//                           ] || 0;

//                         return amount > 0 ? `Tk. ${amount}` : "Free";
//                       })()}
//                       )
//                     </span>
//                     {/* <span style={{ flex: 1 }}>
//                       {category.label} (Tk.
//                       {batchPricing["Arts"]?.[
//                         category.key === "spouse" ? "spouseFee" :
//                         category.key === "guest" ? "guestFee" :
//                         category.key === "driver" ? "driverFee" :
//                         category.key === "childAbove5" ? "above5Fee" : "below5Fee"
//                       ] || 0} )
//                     </span> */}

//                     <div
//                       style={{
//                         display: "flex",
//                         alignItems: "center",
//                         gap: "10px",
//                       }}
//                     >
//                       <button
//                         className="btn"
//                         style={{
//                           backgroundColor: "rgba(255,255,255,0.2)",
//                           border: "1px solid rgba(255,255,255,0.3)",
//                           color: "#fff",
//                           width: "30px",
//                           height: "30px",
//                           display: "flex",
//                           alignItems: "center",
//                           justifyContent: "center",
//                         }}
//                         type="button"
//                         onClick={() => handleAccompanyChange(category.key, -1)}
//                         disabled={
//                           existingRegistration?.paymentStatus === "Paid"
//                         }
//                       >
//                         -
//                       </button>
//                       <span style={{ width: "40px", textAlign: "center" }}>
//                         {formData.accompany[category.key]}
//                       </span>
//                       <button
//                         className="btn"
//                         style={{
//                           backgroundColor: "rgba(255,255,255,0.2)",
//                           border: "1px solid rgba(255,255,255,0.3)",
//                           color: "#fff",
//                           width: "30px",
//                           height: "30px",
//                           display: "flex",
//                           alignItems: "center",
//                           justifyContent: "center",
//                         }}
//                         type="button"
//                         onClick={() => handleAccompanyChange(category.key, 1)}
//                         disabled={
//                           existingRegistration?.paymentStatus === "Paid"
//                         }
//                       >
//                         +
//                       </button>

//                       {/* Only show T-shirt size button for children categories */}
//                       {category.showTshirt && renderSizeButton(category.key)}
//                     </div>
//                   </div>

//                   {/* Display validation errors for children T-shirt sizes */}
//                   {formData.accompany[category.key] > 0 &&
//                     category.showTshirt && (
//                       <div style={{ marginBottom: "10px" }}>
//                         {category.key === "childAbove5" &&
//                           errors.childAbove5TshirtSize && (
//                             <div
//                               className="error-message"
//                               style={{
//                                 color: "#ff6b6b",
//                                 marginTop: "5px",
//                                 fontSize: "12px",
//                               }}
//                             >
//                               {errors.childAbove5TshirtSize}
//                             </div>
//                           )}
//                         {category.key === "childBelow5" &&
//                           errors.childBelow5TshirtSize && (
//                             <div
//                               className="error-message"
//                               style={{
//                                 color: "#ff6b6b",
//                                 marginTop: "5px",
//                                 fontSize: "12px",
//                               }}
//                             >
//                               {errors.childBelow5TshirtSize}
//                             </div>
//                           )}
//                       </div>
//                     )}
//                 </div>
//               ))}
//             </div>
//           </div>

//           {/* T-Shirt Size Modals */}
//           <TShirtSizeModal
//             isOpen={showChildAbove5TShirtModal}
//             onClose={() => setShowChildAbove5TShirtModal(false)}
//             onSelect={(size) =>
//               handleTShirtSizeModalSelect("childAbove5TshirtSize", size)
//             }
//             selectedSizesStr={formData.childAbove5TshirtSize}
//             title="Select T-Shirt Sizes for Children Above 5"
//             maxSelections={formData.accompany.childAbove5}
//           />

//           <TShirtSizeModal
//             isOpen={showChildBelow5TShirtModal}
//             onClose={() => setShowChildBelow5TShirtModal(false)}
//             onSelect={(size) =>
//               handleTShirtSizeModalSelect("childBelow5TshirtSize", size)
//             }
//             selectedSizesStr={formData.childBelow5TshirtSize}
//             title="Select T-Shirt Sizes for Children Below 5"
//             maxSelections={formData.accompany.childBelow5}
//           />

//           {/* Total */}
//           <div
//             className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
//             style={{
//               display: "flex",
//               justifyContent: "space-between",
//               alignItems: "center",
//               fontSize: "1.5rem",
//               marginTop: "20px",
//             }}
//           >
//             <span>Total amount:</span>
//             <span className="amount-title">৳{calculateTotal()}</span>
//           </div>

//           {/* Buttons */}
//           <div className="button-group flex gap-4 mt-6">
//             <button
//               type="button"
//               className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-white text-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
//               onClick={handleSave}
//               disabled={
//                 loading || existingRegistration?.paymentStatus === "Success"
//               }
//             >
//               {loading ? (
//                 <>
//                   <div
//                     className="spinner-border spinner-border-sm"
//                     role="status"
//                   >
//                     <span className="visually-hidden">Loading...</span>
//                   </div>
//                   {existingRegistration ? "Updating..." : "Processing..."}
//                 </>
//               ) : existingRegistration ? (
//                 existingRegistration.paymentStatus === "Success" ? (
//                   "Already Paid"
//                 ) : (
//                   "Submit & Pay Now"
//                 )
//               ) : (
//                 "Submit & Pay Now"
//               )}
//             </button>

//             <button
//               type="button"
//               className="flex justify-center items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
//               onClick={handleHomeClick}
//             >
//               <Home size={18} />
//               Go To Home
//             </button>
//           </div>
//         </form>
//       )}

//       {/* Show only home button if not showing form */}
//       {!showForm && (
//         <div className="flex justify-center text-center mt-4">
//           <button
//             type="button"
//             className="flex items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
//             onClick={handleHomeClick}
//           >
//             <Home size={18} />
//             Go To Home
//           </button>
//         </div>
//       )}

//       {/* Payment Info */}
//       {showForm && (
//         <>
//           <div className="d-flex justify-content-center mt-4">
//             <img
//               src={`${process.env.PUBLIC_URL}/images/payment.jpg`}
//               alt="Payment"
//               className="img-fluid h-25 object-fit-cover rounded-2"
//             />
//           </div>

//           <div className="hero-text mt-8 flex justify-center items-center">
//             <motion.p
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               transition={{ delay: 0.5 }}
//               style={{ fontSize: "16px", color: "#fff" }}
//             >
//               পেমেন্ট সংক্রান্ত টেকনিক্যাল সমস্যায় যোগাযোগ করুন : 01711065316
//             </motion.p>
//           </div>
//         </>
//       )}
//     </div>
//   );
// };

// export default RegistrationPage;

// // import { motion } from "framer-motion";
// // import { ArrowLeft, ArrowRight, Home, Search, X } from "lucide-react";
// // import React, { useEffect, useState } from "react";
// // import { useNavigate, useParams, useSearchParams  } from "react-router-dom";
// // import { useAuth } from "../context/AuthContext";
// // import api from "../services/api";

// // const RegistrationPage = () => {
// //   const { id } = useParams();
// //   const navigate = useNavigate();

// //   // Search state
// //   const [searchMobile, setSearchMobile] = useState("");
// //   const [searchLoading, setSearchLoading] = useState(false);
// //   const [searchError, setSearchError] = useState("");
// //   const [existingRegistration, setExistingRegistration] = useState(null);
// //   const [existingDirectory, setExistingDirectory] = useState(null);
// //   const [showForm, setShowForm] = useState(false);
// //   const [isNewEntry, setIsNewEntry] = useState(false);

// //   // Form data state
// //   const [formData, setFormData] = useState({
// //     name: "",
// //     batch: "",
// //     mobile: "",
// //     email: "",
// //     tshirtSize: "",
// //     accompany: { adult: 0, childBelow5: 0 },
// //     adultTshirtSize: "",
// //     childBelow5TshirtSize: "",
// //   });

// //   const [errors, setErrors] = useState({});
// //   const [showSuccess, setShowSuccess] = useState(false);
// //   const [batchPricing, setBatchPricing] = useState({});
// //   const [tShirtSize, setTShirtSize] = useState([]);
// //   const [loading, setLoading] = useState(false);
// //   const [gender, setGender] = useState("Male");
// //   const [showAdultTShirtModal, setShowAdultTShirtModal] = useState(false);
// //   const [showChildTShirtModal, setShowChildTShirtModal] = useState(false);

// //   const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;
// //   const { user, getAuthHeader } = useAuth();

// //   // Store original mobile from search
// //   const [originalMobile, setOriginalMobile] = useState("");

// //   const stringToArray = (str) => {
// //     if (!str) return [];
// //     return str
// //       .split(",")
// //       .map((s) => s.trim())
// //       .filter((s) => s);
// //   };

// //   const arrayToString = (arr) => {
// //     if (!arr || arr.length === 0) return "";
// //     return arr.join(",");
// //   };

// //   // Fetch batch pricing on mount
// //   useEffect(() => {
// //     const fetchBatchPricing = async () => {
// //       try {
// //         const data = await api.batchPricing.getAll();
// //         const pricingMap = {};
// //         data.forEach((bp) => {
// //           pricingMap[bp.batchName] = {
// //             participantFee: bp.participantFee,
// //             adultFee: bp.adultFee,
// //             below5Fee: bp.below5Fee,
// //           };
// //         });
// //         setBatchPricing(pricingMap);
// //       } catch (err) {
// //         console.error("Error fetching batch pricing:", err);
// //       }
// //     };

// //     fetchBatchPricing();
// //   }, []);

// //   // Fetch T-shirt sizes when gender changes
// //   useEffect(() => {
// //     if (!gender) return;

// //     const fetchTShirtSizes = async () => {
// //       try {
// //         const data = await api.lookup.getTShirtByGender(gender);
// //         const tShirtSizes = data.map((item) => item.name);
// //         setTShirtSize(tShirtSizes);
// //       } catch (err) {
// //         console.error("Error fetching t-shirt sizes:", err);
// //       }
// //     };

// //     fetchTShirtSizes();
// //   }, [gender]);

// //   const [searchParams] = useSearchParams();

// //   // useEffect(() => {
// //   //   const mobileParam = searchParams.get("mobile");
// //   //   if (mobileParam) {
// //   //     setSearchMobile(mobileParam);

// //   //     if (mobileParam.length === 11) {
// //   //       setTimeout(() => {
// //   //         handleSearchRegistration();
// //   //       }, 100);
// //   //     }
// //   //   }
// //   // }, [searchParams]);

// //   // const handleSearchRegistration = async () => {
// //   //   if (!searchMobile || searchMobile.length < 11) {
// //   //     setSearchError("Please enter a valid 11-digit mobile number");
// //   //     return;
// //   //   }

// //   //   try {
// //   //     setSearchLoading(true);
// //   //     setSearchError("");
// //   //     setExistingRegistration(null);
// //   //     setExistingDirectory(null);
// //   //     setIsNewEntry(false);

// //   //     // First, search for existing registration
// //   //     const registrationResponse = await api.registration.getMyRegistrations(searchMobile);

// //   //     if (registrationResponse && registrationResponse.length > 0) {
// //   //       // Found existing registration
// //   //       const registration = registrationResponse[0];
// //   //       setExistingRegistration(registration);

// //   //       // Auto-fill form with registration data
// //   //       setFormData({
// //   //         name: registration.name || "",
// //   //         batch: registration.batch || "",
// //   //         mobile: registration.mobile || searchMobile,
// //   //         email: registration.email || "",
// //   //         tshirtSize: registration.tshirtSize || "",
// //   //         accompany: {
// //   //           adult: registration.accompany?.adult || 0,
// //   //           childBelow5: registration.accompany?.childBelow5 || 0,
// //   //         },
// //   //         adultTshirtSize: registration.adultTshirtSize || "",
// //   //         childBelow5TshirtSize: registration.childBelow5TshirtSize || "",
// //   //       });

// //   //       setOriginalMobile(registration.mobile || searchMobile);
// //   //       setGender(registration.gender || "Male");

// //   //       setShowForm(true);
// //   //       setIsNewEntry(false);

// //   //       // Check payment status
// //   //       if (registration.paymentStatus === "Paid") {
// //   //         setSearchError("This registration is already paid and cannot be modified.");
// //   //       }

// //   //       return; // Stop here if registration found
// //   //     }

// //   //     // If no registration found, search directory
// //   //     const directoryResponse = await api.directory.searchByMobile(searchMobile);

// //   //     if (directoryResponse && directoryResponse.length > 0) {
// //   //       // Found existing directory - use as base for new registration
// //   //       const directory = directoryResponse[0];
// //   //       setExistingDirectory(directory);

// //   //       // Auto-fill form with directory data
// //   //       setFormData({
// //   //         name: directory.name || "",
// //   //         batch: directory.batch || "",
// //   //         mobile: directory.mobile || searchMobile,
// //   //         email: directory.email || "",
// //   //         tshirtSize: "",
// //   //         accompany: { adult: 0, childBelow5: 0 },
// //   //         adultTshirtSize: "",
// //   //         childBelow5TshirtSize: "",
// //   //       });

// //   //       setOriginalMobile(directory.mobile || searchMobile);

// //   //       setShowForm(true);
// //   //       setIsNewEntry(true);
// //   //     } else {
// //   //       // No existing record found - start fresh
// //   //       setFormData({
// //   //         name: "",
// //   //         batch: "",
// //   //         mobile: searchMobile,
// //   //         email: "",
// //   //         tshirtSize: "",
// //   //         accompany: { adult: 0, childBelow5: 0 },
// //   //         adultTshirtSize: "",
// //   //         childBelow5TshirtSize: "",
// //   //       });

// //   //       setOriginalMobile(searchMobile);
// //   //       setShowForm(true);
// //   //       setIsNewEntry(true);
// //   //     }
// //   //   } catch (error) {
// //   //     console.error("Error searching:", error);
// //   //     setSearchError("Failed to search. Please try again.");
// //   //   } finally {
// //   //     setSearchLoading(false);
// //   //   }
// //   // };

// //   // Update the useEffect
// // useEffect(() => {
// //   const mobileParam = searchParams.get("mobile");
// //   if (mobileParam) {
// //     // Set the mobile in state
// //     setSearchMobile(mobileParam);

// //     // Then search with the parameter directly
// //     if (mobileParam.length === 11) {
// //       handleSearchRegistrationWithMobile(mobileParam);
// //     } else {
// //       setSearchError("Mobile number must be 11 digits");
// //     }
// //   }
// // }, [searchParams]);

// // // Create a separate function that accepts mobile parameter
// // const handleSearchRegistrationWithMobile = async (mobileNumber) => {
// //   console.log("Searching for mobile:", mobileNumber);

// //   if (!mobileNumber || mobileNumber.length < 11) {
// //     setSearchError("Please enter a valid 11-digit mobile number");
// //     return;
// //   }

// //   try {
// //     setSearchLoading(true);
// //     setSearchError("");
// //     setExistingRegistration(null);
// //     setExistingDirectory(null);
// //     setIsNewEntry(false);

// //     // First, search for existing registration
// //     const registrationResponse = await api.registration.getMyRegistrations(mobileNumber);

// //     if (registrationResponse && registrationResponse.length > 0) {
// //       // Found existing registration
// //       const registration = registrationResponse[0];
// //       setExistingRegistration(registration);

// //       // Auto-fill form with registration data
// //       setFormData({
// //         name: registration.name || "",
// //         batch: registration.batch || "",
// //         mobile: registration.mobile || mobileNumber,
// //         email: registration.email || "",
// //         tshirtSize: registration.tshirtSize || "",
// //         accompany: {
// //           adult: registration.accompany?.adult || 0,
// //           childBelow5: registration.accompany?.childBelow5 || 0,
// //         },
// //         adultTshirtSize: registration.adultTshirtSize || "",
// //         childBelow5TshirtSize: registration.childBelow5TshirtSize || "",
// //       });

// //       setOriginalMobile(registration.mobile || mobileNumber);
// //       setGender(registration.gender || "Male");

// //       setShowForm(true);
// //       setIsNewEntry(false);

// //       // Check payment status
// //       if (registration.paymentStatus === "Paid") {
// //         setSearchError("This registration is already paid and cannot be modified.");
// //       }

// //       return; // Stop here if registration found
// //     }

// //     // If no registration found, search directory
// //     const directoryResponse = await api.directory.searchByMobile(mobileNumber);

// //     if (directoryResponse && directoryResponse.length > 0) {
// //       // Found existing directory - use as base for new registration
// //       const directory = directoryResponse[0];
// //       setExistingDirectory(directory);

// //       // Auto-fill form with directory data
// //       setFormData({
// //         name: directory.name || "",
// //         batch: directory.batch || "",
// //         mobile: directory.mobile || mobileNumber,
// //         email: directory.email || "",
// //         tshirtSize: "",
// //         accompany: { adult: 0, childBelow5: 0 },
// //         adultTshirtSize: "",
// //         childBelow5TshirtSize: "",
// //       });

// //       setOriginalMobile(directory.mobile || mobileNumber);

// //       setShowForm(true);
// //       setIsNewEntry(true);
// //     } else {
// //       // No existing record found - start fresh
// //       setFormData({
// //         name: "",
// //         batch: "",
// //         mobile: mobileNumber,
// //         email: "",
// //         tshirtSize: "",
// //         accompany: { adult: 0, childBelow5: 0 },
// //         adultTshirtSize: "",
// //         childBelow5TshirtSize: "",
// //       });

// //       setOriginalMobile(mobileNumber);
// //       setShowForm(true);
// //       setIsNewEntry(true);
// //     }
// //   } catch (error) {
// //     console.error("Error searching:", error);
// //     setSearchError("Failed to search. Please try again.");
// //   } finally {
// //     setSearchLoading(false);
// //   }
// // };

// // // Update the original handleSearchRegistration to use the current searchMobile
// // const handleSearchRegistration = async () => {
// //   await handleSearchRegistrationWithMobile(searchMobile);
// // };

// //   const handleResetSearch = () => {
// //     setSearchMobile("");
// //     setExistingRegistration(null);
// //     setExistingDirectory(null);
// //     setShowForm(false);
// //     setIsNewEntry(false);
// //     setFormData({
// //       name: "",
// //       batch: "",
// //       mobile: "",
// //       email: "",
// //       tshirtSize: "",
// //       accompany: { adult: 0, childBelow5: 0 },
// //       adultTshirtSize: "",
// //       childBelow5TshirtSize: "",
// //     });
// //     setOriginalMobile("");
// //     setGender("Male");
// //     setSearchError("");
// //   };

// //   // Ensure mobile field is always set to originalMobile
// //   useEffect(() => {
// //     if (originalMobile) {
// //       setFormData(prev => ({
// //         ...prev,
// //         mobile: originalMobile
// //       }));
// //     }
// //   }, [originalMobile]);

// //   const handleInputChange = (field, value) => {
// //     if (field === "mobile") {
// //       return; // Don't allow mobile to be changed
// //     }

// //     setFormData((prev) => ({ ...prev, [field]: value }));

// //     if (errors[field]) {
// //       setErrors((prev) => ({ ...prev, [field]: "" }));
// //     }
// //   };

// //   const handleAccompanyChange = (type, inc) => {
// //     const newCount = Math.max(0, formData.accompany[type] + inc);

// //     setFormData((prev) => {
// //       const sizeField =
// //         type === "adult" ? "adultTshirtSize" : "childBelow5TshirtSize";
// //       const currentSizes = stringToArray(prev[sizeField]);

// //       let updatedSizes = currentSizes;
// //       if (newCount < prev.accompany[type]) {
// //         updatedSizes = currentSizes.slice(0, newCount);
// //       } else if (newCount === 0) {
// //         updatedSizes = [];
// //       }

// //       return {
// //         ...prev,
// //         accompany: {
// //           ...prev.accompany,
// //           [type]: newCount,
// //         },
// //         [sizeField]: arrayToString(updatedSizes),
// //       };
// //     });
// //   };

// //   const handleTShirtSizeModalSelect = (type, size) => {
// //     const currentSizes = stringToArray(formData[type]);
// //     const maxSelections =
// //       formData.accompany[type === "adultTshirtSize" ? "adult" : "childBelow5"];

// //     if (currentSizes.includes(size)) {
// //       const newSizes = currentSizes.filter((s) => s !== size);
// //       setFormData((prev) => ({
// //         ...prev,
// //         [type]: arrayToString(newSizes),
// //       }));
// //     } else {
// //       if (currentSizes.length < maxSelections) {
// //         const newSizes = [...currentSizes, size];
// //         setFormData((prev) => ({
// //           ...prev,
// //           [type]: arrayToString(newSizes),
// //         }));
// //       }
// //     }
// //   };

// //   const validateForm = () => {
// //     const newErrors = {};
// //     if (!formData.name.trim()) newErrors.name = "Name is required";
// //     if (!formData.batch) newErrors.batch = "Batch selection required";

// //     // Mobile validation - always use originalMobile
// //     const mobileToValidate = originalMobile || formData.mobile;
// //     if (!mobileToValidate || !mobileToValidate.trim()) {
// //       newErrors.mobile = "Mobile number is required";
// //     } else if (!/^\d{11}$/.test(mobileToValidate)) {
// //       newErrors.mobile = "Must be 11 digits";
// //     }

// //     if (!formData.email.trim()) newErrors.email = "Email required";
// //     if (!formData.tshirtSize) newErrors.tshirtSize = "T-shirt size required";

// //     // Validate adult t-shirt sizes
// //     const adultSizes = stringToArray(formData.adultTshirtSize);
// //     if (
// //       formData.accompany.adult > 0 &&
// //       adultSizes.length !== formData.accompany.adult
// //     ) {
// //       newErrors.adultTshirtSize = `Please select ${formData.accompany.adult} t-shirt size(s) for ${formData.accompany.adult} adult(s)`;
// //     }

// //     // Validate child t-shirt sizes
// //     const childSizes = stringToArray(formData.childBelow5TshirtSize);
// //     if (
// //       formData.accompany.childBelow5 > 0 &&
// //       childSizes.length !== formData.accompany.childBelow5
// //     ) {
// //       newErrors.childBelow5TshirtSize = `Please select ${formData.accompany.childBelow5} t-shirt size(s) for ${formData.accompany.childBelow5} child(ren)`;
// //     }

// //     setErrors(newErrors);
// //     return Object.keys(newErrors).length === 0;
// //   };

// //   const calculateTotal = () => {
// //     const batch = batchPricing[formData.batch];
// //     if (!batch) return 0;

// //     return (
// //       batch.participantFee +
// //       formData.accompany.adult * batch.adultFee +
// //       formData.accompany.childBelow5 * batch.below5Fee
// //     );
// //   };

// //   const handleSubmit = async () => {
// //     // Always use originalMobile for validation and submission
// //     const mobileToUse = originalMobile || formData.mobile;

// //     if (!validateForm()) {
// //       console.log("Validation failed with errors:", errors);
// //       return;
// //     }

// //     // Double-check required fields
// //     if (!formData.name || !formData.name.trim()) {
// //       alert("Please enter a name");
// //       return;
// //     }

// //     if (!mobileToUse || !/^\d{11}$/.test(mobileToUse)) {
// //       alert("Please ensure mobile number is 11 digits");
// //       return;
// //     }

// //     setLoading(true);
// //     try {
// //       const formDataToSend = new FormData();
// //       formDataToSend.append("name", formData.name);
// //       formDataToSend.append("batch", formData.batch);
// //       formDataToSend.append("mobile", mobileToUse);
// //       formDataToSend.append("email", formData.email);
// //       formDataToSend.append("tshirtSize", formData.tshirtSize);
// //       formDataToSend.append("gender", gender);
// //       formDataToSend.append("accompanyAdult", formData.accompany.adult);
// //       formDataToSend.append(
// //         "accompanyChildBelow5",
// //         formData.accompany.childBelow5
// //       );
// //       formDataToSend.append("adultTshirtSize", formData.adultTshirtSize || "");
// //       formDataToSend.append(
// //         "childBelow5TshirtSize",
// //         formData.childBelow5TshirtSize || ""
// //       );

// //       if (user?.id) {
// //         formDataToSend.append("userId", user.id.toString());
// //       }

// //       let data;
// //       if (existingRegistration && existingRegistration.id) {
// //         // Update existing registration
// //         data = await api.registration.update(existingRegistration.id, formDataToSend);
// //       } else {
// //         // Create new registration
// //         data = await api.registration.create(formDataToSend);
// //       }

// //       setShowSuccess(true);
// //       setTimeout(() => setShowSuccess(false), 3000);

// //       return data;
// //     } catch (error) {
// //       console.error("Error submitting registration:", error);
// //       alert(`Registration ${existingRegistration ? "update" : "failed"}: ${error.message}`);
// //       return false;
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   const handlePayment = async (participantData) => {
// //     try {
// //       const payload = {
// //         name: formData.name,
// //         batch: formData.batch,
// //         mobile: originalMobile || formData.mobile,
// //         email: formData.email,
// //         tshirtSize: formData.tshirtSize,
// //         accompanyAdult: formData.accompany.adult,
// //         accompanyChildBelow5: formData.accompany.childBelow5,
// //         totalAmount: calculateTotal(),
// //         order_id: participantData?.orderId || `order_${Date.now()}`,
// //         participantId: participantData?.id || (existingRegistration?.id),
// //         userId: user?.id,
// //         adultTshirtSize: formData.adultTshirtSize,
// //         childBelow5TshirtSize: formData.childBelow5TshirtSize,
// //       };

// //       const data = await api.payment.create(payload);

// //       if (data.checkout_url) {
// //         window.location.href = data.checkout_url;
// //       } else {
// //         alert(data.error || "Failed to get checkout URL");
// //       }
// //     } catch (error) {
// //       console.error("Payment request failed", error);
// //       alert("Payment request failed. Try again.");
// //     }
// //   };

// //   const handleSave = async () => {
// //     // Check if existing registration is paid
// //     if (existingRegistration && existingRegistration.paymentStatus === "Paid") {
// //       alert("This registration is already paid and cannot be modified.");
// //       return;
// //     }

// //     const submitted = await handleSubmit();
// //     if (submitted) {
// //       await handlePayment(submitted);
// //     }
// //   };

// //   const TShirtSizeModal = ({
// //     isOpen,
// //     onClose,
// //     onSelect,
// //     selectedSizesStr,
// //     title,
// //     maxSelections,
// //   }) => {
// //     if (!isOpen) return null;

// //     const selectedSizes = stringToArray(selectedSizesStr);

// //     return (
// //       <div
// //         style={{
// //           position: "fixed",
// //           top: 0,
// //           left: 0,
// //           right: 0,
// //           bottom: 0,
// //           backgroundColor: "rgba(0,0,0,0.7)",
// //           display: "flex",
// //           justifyContent: "center",
// //           alignItems: "center",
// //           zIndex: 1000,
// //         }}
// //       >
// //         <div
// //           style={{
// //             backgroundColor: "#cffae0",
// //             padding: "20px",
// //             borderRadius: "10px",
// //             width: "90%",
// //             maxWidth: "500px",
// //             border: "1px solid rgba(255,255,255,0.2)",
// //           }}
// //         >
// //           <h3
// //             style={{
// //               color: "#1a3625",
// //               marginBottom: "10px",
// //               textAlign: "center",
// //             }}
// //           >
// //             {title}
// //           </h3>
// //           <p
// //             style={{
// //               color: "black",
// //               textAlign: "center",
// //               marginBottom: "20px",
// //             }}
// //           >
// //             Select {maxSelections} size(s) for {maxSelections} person(s)
// //           </p>

// //           <div
// //             style={{
// //               display: "grid",
// //               gridTemplateColumns: "repeat(2, 1fr)",
// //               gap: "10px",
// //               marginBottom: "20px",
// //             }}
// //           >
// //             {tShirtSize.map((size) => {
// //               const isSelected = selectedSizes.includes(size);
// //               const isDisabled =
// //                 !isSelected && selectedSizes.length >= maxSelections;

// //               return (
// //                 <button
// //                   key={size}
// //                   onClick={() => onSelect(size)}
// //                   disabled={isDisabled}
// //                   style={{
// //                     padding: "12px",
// //                     backgroundColor: isSelected
// //                       ? "rgba(6, 148, 34,1)"
// //                       : "rgba(6, 148, 62,1)",
// //                     border: `2px solid ${
// //                       isSelected ? "rgba(6, 148, 34,0.5)" : "rgba(0,0,0,0.2)"
// //                     }`,
// //                     color: isDisabled ? "black" : "white",
// //                     borderRadius: "5px",
// //                     cursor: isDisabled ? "not-allowed" : "pointer",
// //                     transition: "all 0.3s",
// //                     opacity: isDisabled ? 0.7 : 1,
// //                     position: "relative",
// //                   }}
// //                 >
// //                   {size}
// //                   {isSelected && (
// //                     <span
// //                       style={{
// //                         position: "absolute",
// //                         top: "5px",
// //                         right: "5px",
// //                         fontSize: "12px",
// //                         color: "white",
// //                       }}
// //                     >
// //                       ✓
// //                     </span>
// //                   )}
// //                 </button>
// //               );
// //             })}
// //           </div>

// //           <div
// //             style={{
// //               display: "flex",
// //               justifyContent: "space-between",
// //               alignItems: "center",
// //               marginBottom: "20px",
// //             }}
// //           >
// //             <div style={{ color: "black" }}>
// //               Selected: {selectedSizes.length}/{maxSelections}
// //             </div>
// //             <div style={{ color: "black" }}>
// //               {selectedSizes.length > 0 && (
// //                 <div>Sizes: {selectedSizes.join(", ")}</div>
// //               )}
// //             </div>
// //           </div>

// //           <div style={{ display: "flex", justifyContent: "center" }}>
// //             <button
// //               onClick={onClose}
// //               style={{
// //                 padding: "10px 30px",
// //                 backgroundColor: "rgba(222, 181, 35,1)",
// //                 border: "1px solid rgba(222, 181, 35,0.8)",
// //                 color: "white",
// //                 borderRadius: "5px",
// //                 cursor: "pointer",
// //               }}
// //             >
// //               Done
// //             </button>
// //           </div>
// //         </div>
// //       </div>
// //     );
// //   };

// //   const renderSizeButton = (type) => {
// //     const sizesStr =
// //       type === "adult"
// //         ? formData.adultTshirtSize
// //         : formData.childBelow5TshirtSize;
// //     const sizes = stringToArray(sizesStr);
// //     const count =
// //       formData.accompany[type === "adult" ? "adult" : "childBelow5"];

// //     if (count === 0) return null;

// //     return (
// //       <div style={{ marginLeft: "10px", minWidth: "120px" }}>
// //         <button
// //           type="button"
// //           onClick={() =>
// //             type === "adult"
// //               ? setShowAdultTShirtModal(true)
// //               : setShowChildTShirtModal(true)
// //           }
// //           style={{
// //             padding: "6px 12px",
// //             backgroundColor: "rgba(255,255,255,0.2)",
// //             border: "1px solid rgba(255,255,255,0.3)",
// //             color: "#fff",
// //             borderRadius: "5px",
// //             fontSize: "12px",
// //             cursor: "pointer",
// //             width: "100%",
// //             textAlign: "left",
// //           }}
// //           disabled={existingRegistration?.paymentStatus === "Paid"}
// //         >
// //           {sizes.length === 0 ? (
// //             `Select ${count} T-Shirt(s)`
// //           ) : (
// //             <div>
// //               <div>Sizes: {sizes.join(", ")}</div>
// //               <div style={{ fontSize: "10px", opacity: 0.8 }}>
// //                 {sizes.length} of {count} selected
// //               </div>
// //             </div>
// //           )}
// //         </button>
// //       </div>
// //     );
// //   };

// //   const handleHomeClick = () => {
// //     navigate("/home");
// //   };

// //   return (
// //     <div className="form-container px-4">
// //       <h2 className="form-title m-0">Event Registration</h2>
// //       <h5
// //         style={{ textAlign: "center", color: "#58db8c", marginBottom: "20px" }}
// //       >
// //         KGC'97 Day 2026
// //       </h5>

// //       {showSuccess && (
// //         <div className="success-message">
// //           🎉 Registration {existingRegistration ? "updated" : "submitted"} successfully!
// //         </div>
// //       )}

// //       {/* Mobile Search Section */}
// //       {!showForm && (
// //         <div className="search-section mb-6">
// //           <div className="text-center mb-2">
// //             <p className="text-gray-300 text-white text-lg">
// //               Enter your 11 digits mobile number
// //             </p>
// //           </div>

// //           <div className="row justify-content-center">
// //             <div className="col-12 col-md-8 col-lg-6">
// //               <div className="input-group input-group-lg">
// //                 <input
// //                   type="text"
// //                   className="form-control bg-light text-dark border-green-500"
// //                   placeholder="01XXXXXXXX"
// //                   value={searchMobile}
// //                   onChange={(e) => {
// //                     const value = e.target.value.replace(/\D/g, "");
// //                     if (value.length <= 11) {
// //                       setSearchMobile(value);
// //                       setSearchError("");
// //                     }
// //                   }}
// //                   maxLength="11"
// //                   style={{ height: "50px" }}
// //                 />
// //                 <button
// //                   className="btn btn-primary d-flex align-items-center gap-2"
// //                   onClick={handleSearchRegistration}
// //                   disabled={searchLoading || !searchMobile.trim()}
// //                   style={{ height: "50px", width: "60px",
// //                     padding: "0 15px", }}
// //                   title="Search"
// //                 >
// //                   {searchLoading ? (
// //                     <>
// //                       <span className="spinner-border spinner-border-sm" />
// //                       Searching...
// //                     </>
// //                   ) : (
// //                     <ArrowRight size={24} />
// //                   )}
// //                 </button>
// //               </div>
// //               {searchError && (
// //                 <div className="text-danger small mt-2 text-center">
// //                   {searchError}
// //                 </div>
// //               )}
// //             </div>
// //           </div>
// //         </div>
// //       )}

// //       {/* Show form after search */}
// //       {showForm && (
// //         <form onSubmit={(e) => e.preventDefault()}>
// //           {/* Existing Registration Notification */}
// //           {existingRegistration && (
// //             <div className="alert alert-info mb-4">
// //               <div className="d-flex align-items-center justify-content-between">
// //                 <div>
// //                   <strong>✓ Registration Found</strong>
// //                   <div className="small">
// //                     {existingRegistration.paymentStatus === "Paid"
// //                       ? "This registration is already paid. View details below."
// //                       : "You can update this registration."}
// //                   </div>
// //                   {existingRegistration.paymentStatus === "Paid" && (
// //                     <div className="small text-warning mt-1">
// //                       Paid registrations cannot be modified
// //                     </div>
// //                   )}
// //                 </div>

// //               </div>
// //             </div>
// //           )}

// //           {/* Existing Directory Notification */}
// //           {existingDirectory && !existingRegistration && (
// //             <div className="alert alert-secondary mb-4">
// //               <div className="d-flex align-items-center justify-content-between">
// //                 <div>
// //                   <strong>📋 Directory Entry Found</strong>
// //                   <div className="small">
// //                     Found directory entry for this mobile. Some fields are pre-filled.
// //                   </div>
// //                 </div>
// //                 {/* <button
// //                   type="button"
// //                   className="btn btn-sm btn-outline-secondary"
// //                   onClick={handleResetSearch}
// //                 >
// //                   Search Different Mobile
// //                 </button> */}
// //               </div>
// //             </div>
// //           )}

// //           {/* New Entry Notification */}
// //           {isNewEntry && !existingRegistration && !existingDirectory && (
// //             <div className="alert alert-warning mb-4">
// //               <div className="d-flex align-items-center justify-content-between">
// //                 <div>
// //                   <strong>➕ Create New Registration</strong>
// //                   <div className="small">
// //                     No existing record found for mobile: {originalMobile}
// //                   </div>
// //                 </div>
// //                 {/* <button
// //                   type="button"
// //                   className="btn btn-sm btn-outline-warning"
// //                   onClick={handleResetSearch}
// //                 >
// //                   Search Different Mobile
// //                 </button> */}
// //               </div>
// //             </div>
// //           )}

// //           {/* Mobile Field - Fixed display */}
// //           <div className="form-group">
// //             <label className="form-label">Mobile Number*</label>
// //             <div className="mobile-display">
// //               <div
// //                 style={{
// //                   padding: "12px",
// //                   backgroundColor: "#e9ecef",
// //                   color: "#495057",
// //                   borderRadius: "5px",
// //                   border: "1px solid #dee2e6",
// //                   fontSize: "16px",
// //                   fontWeight: "bold",
// //                 }}
// //               >
// //                 {originalMobile || "No mobile number"}
// //               </div>
// //               <input
// //                 type="hidden"
// //                 name="mobile"
// //                 value={originalMobile || ""}
// //               />
// //             </div>
// //             {errors.mobile && (
// //               <div className="error-message">{errors.mobile}</div>
// //             )}
// //           </div>

// //           {/* Name */}
// //           <div className="form-group">
// //             <label className="form-label">Name of Participant *</label>
// //             <input
// //               type="text"
// //               className="form-input"
// //               placeholder="Enter your full name"
// //               value={formData.name}
// //               onChange={(e) => handleInputChange("name", e.target.value)}
// //               disabled={existingRegistration?.paymentStatus === "Paid"}
// //               readOnly={existingRegistration?.paymentStatus === "Paid"}
// //               style={
// //                 existingRegistration?.paymentStatus === "Paid"
// //                   ? { backgroundColor: "#e9ecef", color: "#495057" }
// //                   : {}
// //               }
// //             />
// //             {errors.name && <div className="error-message">{errors.name}</div>}
// //           </div>

// //           {/* Batch */}
// //           <div style={{ width: "100%", position: "relative" }}>
// //             <label className="form-label">Group *</label>
// //             <select
// //               value={formData.batch}
// //               onChange={(e) => handleInputChange("batch", e.target.value)}
// //               disabled={existingRegistration?.paymentStatus === "Paid"}
// //               style={{
// //                 width: "100%",
// //                 padding: "8px 15px",
// //                 borderRadius: "0px",
// //                 border: "1px solid rgba(255, 255, 255, 0.2)",
// //                 background: existingRegistration?.paymentStatus === "Paid"
// //                   ? "#e9ecef"
// //                   : "rgba(255,255,255,0.1)",
// //                 color: existingRegistration?.paymentStatus === "Paid"
// //                   ? "#495057"
// //                   : "white",
// //                 fontSize: "1rem",
// //                 appearance: "none",
// //                 WebkitAppearance: "none",
// //                 MozAppearance: "none",
// //                 backdropFilter: "blur(8px)",
// //                 transition: "all 0.3s ease",
// //                 backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
// //                   existingRegistration?.paymentStatus === "Paid" ? "#495057" : "white"
// //                 }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
// //                 backgroundRepeat: "no-repeat",
// //                 backgroundPosition: "right 12px center",
// //                 backgroundSize: "1rem",
// //                 marginBottom: "15px",
// //               }}
// //             >
// //               <option
// //                 value=""
// //                 style={{ backgroundColor: "#cffae0", color: "black" }}
// //               >
// //                 Select your group
// //               </option>
// //               {Object.keys(batchPricing).map((batch) => (
// //                 <option
// //                   key={batch}
// //                   value={batch}
// //                   style={{ backgroundColor: "#cffae0", color: "black" }}
// //                 >
// //                   {batch}
// //                 </option>
// //               ))}
// //             </select>
// //           </div>

// //           {/* Email */}
// //           <div className="form-group">
// //             <label className="form-label">Email *</label>
// //             <input
// //               type="email"
// //               className="form-input"
// //               placeholder="your.email@example.com"
// //               value={formData.email}
// //               onChange={(e) => handleInputChange("email", e.target.value)}
// //               disabled={existingRegistration?.paymentStatus === "Paid"}
// //               readOnly={existingRegistration?.paymentStatus === "Paid"}
// //               style={
// //                 existingRegistration?.paymentStatus === "Paid"
// //                   ? { backgroundColor: "#e9ecef", color: "#495057" }
// //                   : {}
// //               }
// //             />
// //             {errors.email && <div className="error-message">{errors.email}</div>}
// //           </div>

// //           {/* Gender Selection */}
// //           <div className="form-group">
// //             <label className="form-label">Select Gender *</label>
// //             <div
// //               style={{
// //                 display: "flex",
// //                 gap: "20px",
// //                 color: "white",
// //                 marginBottom: "15px",
// //               }}
// //             >
// //               <label
// //                 style={{ display: "flex", alignItems: "center", gap: "5px" }}
// //               >
// //                 <input
// //                   type="radio"
// //                   name="gender"
// //                   value="Male"
// //                   checked={gender === "Male"}
// //                   onChange={(e) => setGender(e.target.value)}
// //                   disabled={existingRegistration?.paymentStatus === "Paid"}
// //                 />
// //                 Male
// //               </label>
// //               <label
// //                 style={{ display: "flex", alignItems: "center", gap: "5px" }}
// //               >
// //                 <input
// //                   type="radio"
// //                   name="gender"
// //                   value="Female"
// //                   checked={gender === "Female"}
// //                   onChange={(e) => setGender(e.target.value)}
// //                   disabled={existingRegistration?.paymentStatus === "Paid"}
// //                 />
// //                 Female
// //               </label>
// //             </div>
// //           </div>

// //           {/* T-shirt Size */}
// //           <div style={{ width: "100%", position: "relative" }}>
// //             <label className="form-label">T-Shirt Size *</label>
// //             <select
// //               value={formData.tshirtSize}
// //               onChange={(e) => handleInputChange("tshirtSize", e.target.value)}
// //               disabled={existingRegistration?.paymentStatus === "Paid"}
// //               style={{
// //                 width: "100%",
// //                 padding: "8px 15px",
// //                 borderRadius: "0px",
// //                 border: "1px solid rgba(255, 255, 255, 0.2)",
// //                 background: existingRegistration?.paymentStatus === "Paid"
// //                   ? "#e9ecef"
// //                   : "rgba(255,255,255,0.1)",
// //                 color: existingRegistration?.paymentStatus === "Paid"
// //                   ? "#495057"
// //                   : "white",
// //                 fontSize: "1rem",
// //                 appearance: "none",
// //                 WebkitAppearance: "none",
// //                 MozAppearance: "none",
// //                 backdropFilter: "blur(8px)",
// //                 transition: "all 0.3s ease",
// //                 backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
// //                   existingRegistration?.paymentStatus === "Paid" ? "#495057" : "white"
// //                 }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
// //                 backgroundRepeat: "no-repeat",
// //                 backgroundPosition: "right 12px center",
// //                 backgroundSize: "1rem",
// //                 marginBottom: "15px",
// //               }}
// //             >
// //               <option
// //                 value=""
// //                 style={{ backgroundColor: "#cffae0", color: "black" }}
// //               >
// //                 Select your T-Shirt Size
// //               </option>
// //               {tShirtSize.map((size) => (
// //                 <option
// //                   key={size}
// //                   value={size}
// //                   style={{ backgroundColor: "#cffae0", color: "black" }}
// //                 >
// //                   {size}
// //                 </option>
// //               ))}
// //             </select>
// //             {errors.tshirtSize && (
// //               <div className="error-message">{errors.tshirtSize}</div>
// //             )}
// //           </div>

// //           {/* Accompany Section */}
// //           <div className="form-group">
// //             <label className="form-label">Accompanying Persons</label>
// //             <div
// //               className="accompany-section"
// //               style={{
// //                 padding: "15px",
// //                 borderRadius: "12px",
// //                 background: "rgba(255,255,255,0.2)",
// //                 border: "1px solid rgba(255, 255, 255, 0.2)",
// //                 opacity: existingRegistration?.paymentStatus === "Paid" ? 0.6 : 1,
// //               }}
// //             >
// //               {["adult", "childBelow5"].map((type) => (
// //                 <div key={type}>
// //                   <div
// //                     style={{
// //                       display: "flex",
// //                       alignItems: "center",
// //                       justifyContent: "space-between",
// //                       marginBottom: "15px",
// //                       color: "#fff",
// //                     }}
// //                   >
// //                     <span style={{ flex: 1 }}>
// //                       {type === "adult" ? "Adult" : "Children Below 5"} (৳
// //                       {batchPricing[formData.batch]?.[
// //                         type === "adult" ? "adultFee" : "below5Fee"
// //                       ] || 0}{" "}
// //                       each )
// //                     </span>

// //                     <div
// //                       style={{
// //                         display: "flex",
// //                         alignItems: "center",
// //                         gap: "10px",
// //                       }}
// //                     >
// //                       <button
// //                         className="btn"
// //                         style={{
// //                           backgroundColor: "rgba(255,255,255,0.2)",
// //                           border: "1px solid rgba(255,255,255,0.3)",
// //                           color: "#fff",
// //                           width: "30px",
// //                           height: "30px",
// //                           display: "flex",
// //                           alignItems: "center",
// //                           justifyContent: "center",
// //                         }}
// //                         type="button"
// //                         onClick={() => handleAccompanyChange(type, -1)}
// //                         disabled={existingRegistration?.paymentStatus === "Paid"}
// //                       >
// //                         -
// //                       </button>
// //                       <span style={{ width: "40px", textAlign: "center" }}>
// //                         {formData.accompany[type]}
// //                       </span>
// //                       <button
// //                         className="btn"
// //                         style={{
// //                           backgroundColor: "rgba(255,255,255,0.2)",
// //                           border: "1px solid rgba(255,255,255,0.3)",
// //                           color: "#fff",
// //                           width: "30px",
// //                           height: "30px",
// //                           display: "flex",
// //                           alignItems: "center",
// //                           justifyContent: "center",
// //                         }}
// //                         type="button"
// //                         onClick={() => handleAccompanyChange(type, 1)}
// //                         disabled={existingRegistration?.paymentStatus === "Paid"}
// //                       >
// //                         +
// //                       </button>

// //                       {renderSizeButton(type)}
// //                     </div>
// //                   </div>

// //                   {/* Display selected sizes */}
// //                   {formData.accompany[type] > 0 && (
// //                     <div style={{ marginBottom: "10px" }}>
// //                       {type === "adult" && errors.adultTshirtSize && (
// //                         <div
// //                           className="error-message"
// //                           style={{
// //                             color: "#ff6b6b",
// //                             marginTop: "5px",
// //                             fontSize: "12px",
// //                           }}
// //                         >
// //                           {errors.adultTshirtSize}
// //                         </div>
// //                       )}
// //                       {type === "childBelow5" && errors.childBelow5TshirtSize && (
// //                         <div
// //                           className="error-message"
// //                           style={{
// //                             color: "#ff6b6b",
// //                             marginTop: "5px",
// //                             fontSize: "12px",
// //                           }}
// //                         >
// //                           {errors.childBelow5TshirtSize}
// //                         </div>
// //                       )}
// //                     </div>
// //                   )}
// //                 </div>
// //               ))}
// //             </div>
// //           </div>

// //           {/* T-Shirt Size Modals */}
// //           <TShirtSizeModal
// //             isOpen={showAdultTShirtModal}
// //             onClose={() => setShowAdultTShirtModal(false)}
// //             onSelect={(size) =>
// //               handleTShirtSizeModalSelect("adultTshirtSize", size)
// //             }
// //             selectedSizesStr={formData.adultTshirtSize}
// //             title="Select Adult T-Shirt Sizes"
// //             maxSelections={formData.accompany.adult}
// //           />

// //           <TShirtSizeModal
// //             isOpen={showChildTShirtModal}
// //             onClose={() => setShowChildTShirtModal(false)}
// //             onSelect={(size) =>
// //               handleTShirtSizeModalSelect("childBelow5TshirtSize", size)
// //             }
// //             selectedSizesStr={formData.childBelow5TshirtSize}
// //             title="Select Child T-Shirt Sizes"
// //             maxSelections={formData.accompany.childBelow5}
// //           />

// //           {/* Total */}
// //           <div
// //             className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
// //             style={{
// //               display: "flex",
// //               justifyContent: "space-between",
// //               alignItems: "center",
// //               fontSize: "1.5rem",
// //               marginTop: "20px",
// //             }}
// //           >
// //             <span>Total amount:</span>
// //             <span className="amount-title">৳{calculateTotal()}</span>
// //           </div>

// //           {/* Buttons */}
// //           <div className="button-group flex gap-4 mt-6">
// //             <button
// //               type="button"
// //               className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-white text-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
// //               onClick={handleSave}
// //               disabled={loading || existingRegistration?.paymentStatus === "Success"}
// //             >
// //               {loading ? (
// //                 <>
// //                   <div className="spinner-border spinner-border-sm" role="status">
// //                     <span className="visually-hidden">Loading...</span>
// //                   </div>
// //                   {existingRegistration ? "Updating..." : "Processing..."}
// //                 </>
// //               ) : existingRegistration ? (
// //                 existingRegistration.paymentStatus === "Success"
// //                   ? "Already Paid"
// //                   : "Submit & Pay Now"
// //               ) : (
// //                 "Submit & Pay Now"
// //               )}
// //             </button>

// //             {/* <button
// //               type="button"
// //               className="flex items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
// //               onClick={handleResetSearch}
// //               disabled={loading}
// //             >
// //               <X size={18} />
// //               New Search
// //             </button> */}
// //             <button
// //             type="button"
// //             className="flex items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
// //             onClick={handleHomeClick}
// //           >
// //             <Home size={18} />
// //             Go To Home
// //           </button>
// //           </div>
// //         </form>
// //       )}

// //       {/* Show only home button if not showing form */}
// //       {!showForm && (
// //         <div className="flex justify-center text-center mt-4">
// //           <button
// //             type="button"
// //             className="flex items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
// //             onClick={handleHomeClick}
// //           >
// //             <Home size={18} />
// //             Go To Home
// //           </button>
// //         </div>
// //       )}

// //       {/* Payment Info */}
// //       {showForm && (
// //         <>
// //           <div className="d-flex justify-content-center mt-4">
// //             <img
// //               src={`${process.env.PUBLIC_URL}/images/payment.jpg`}
// //               alt="Payment"
// //               className="img-fluid h-25 object-fit-cover rounded-2"
// //             />
// //           </div>

// //           <div className="hero-text mt-8 flex justify-center items-center">
// //             <motion.p
// //               initial={{ opacity: 0 }}
// //               animate={{ opacity: 1 }}
// //               transition={{ delay: 0.5 }}
// //               style={{ fontSize: "16px", color: "#fff" }}
// //             >
// //               পেমেন্ট সংক্রান্ত টেকনিক্যাল সমস্যায় যোগাযোগ করুন : 01711065316
// //             </motion.p>
// //           </div>
// //         </>
// //       )}
// //     </div>
// //   );
// // };

// // export default RegistrationPage;
